import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import homeMascotImage from "./assets/vibe-b/home-mascot.png";
import missionAiImage from "./assets/vibe-b/mission-ai.png";
import missionReadingImage from "./assets/vibe-b/mission-reading.png";
import missionSpeakingImage from "./assets/vibe-b/mission-speaking.png";
import readingBookCoverImage from "./assets/vibe-b/reading-book-cover.png";
import readingPreparingBearImage from "./assets/vibe-b/reading-preparing-bear.png";
import readingReadyBearImage from "./assets/vibe-b/reading-ready-bear.png";
import readingResultCorrectImage from "./assets/vibe-b/reading-result-correct.png";
import readingResultWrongImage from "./assets/vibe-b/reading-result-wrong.png";
import speakingMirroringLogo from "./assets/speaking-mirroring-logo.svg";
import speakingPreparingBearImage from "./assets/vibe-b/speaking-preparing-bear.png";
import speakingQuestionBearImage from "./assets/vibe-b/speaking-question-bear.png";
import speakingReadyBearImage from "./assets/vibe-b/speaking-ready-bear.png";
import studyStampOneImage from "./assets/vibe-b/study-stamp-1.png";
import studyStampTwoImage from "./assets/vibe-b/study-stamp-2.png";
import studyStampThreeImage from "./assets/vibe-b/study-stamp-3.png";

type ApiEnvelope<T = unknown> = {
  ok: boolean;
  message: string;
  data?: T;
  [key: string]: unknown;
};

type ApiErrorShape = {
  status: number;
  message: string;
  payload?: ApiEnvelope | null;
};

type VibeUser = {
  id: string;
  email: string;
  name: string;
  phone: string;
  gender: string;
  className?: string;
  birth?: string;
  thumbnailUrl?: string;
  provider?: "email" | "kakao" | string;
  authToken?: string;
  authTokenExpiresAt?: number;
};

type SignupProfilePayload = {
  id: string;
  password: string;
  passwordConfirm: string;
  name: string;
  phone: string;
  gender: string;
  birth: string;
};

type SignupPayload = SignupProfilePayload & {
  email: string;
};

type VibeNotice = {
  id: string;
  title: string;
  description: string;
  date: string;
  body: string[];
  category: "notice" | "system" | "service";
};

type VibeReview = {
  id: string;
  kind: "reading" | "speaking";
  title: string;
  summary: string;
  adminName: string;
  assignedAt: string;
  lessonDate?: string;
  score: number;
  status: "new" | "reviewed";
  passage: string;
  coachComment: string;
  bookImageUrl?: string;
  questions?: Array<{
    id: string;
    sourceReviewId?: string;
    sourceTitle?: string;
    category?: string;
    prompt: string;
    sentence?: string;
    correctAnswer: string;
    explanation?: string;
    sceneKo?: string;
    sceneEn?: string;
    choices?: string[];
  }>;
  wrongItems: Array<{
    id: string;
    prompt: string;
    correctAnswer: string;
    userAnswer: string;
    explanation: string;
    sourceTitle: string;
    category: string;
  }>;
};

type VibeTicket = {
  code: string;
  registeredAt: string;
  expiresAt: string;
  isExpired: boolean;
};

type VibeInquiry = {
  id: string;
  category: string;
  title: string;
  body: string;
  answer?: string;
  status: "waiting" | "answered";
  createdAt: string;
};

type RecentLearningItem = {
  id: string;
  title: string;
  date: string;
  kind: "reading" | "speaking";
  status: "new" | "reviewed";
};

type TicketSubmitResult = {
  ok: boolean;
  message: string;
  ticket?: VibeTicket | null;
};

type MirroringQuestion = {
  id: string;
  sourceTitle: string;
  category: string;
  prompt: string;
  sentence: string;
  choices: string[];
  correctAnswer?: string;
  explanation: string;
};

type MirroringQuestionPayload = {
  questionId: string;
  question: MirroringQuestion;
  index: number;
  total: number;
};

type MirroringAnalysisResult = {
  readingScore: number;
  speakingScore: number;
  noteScore: number;
  strengthPoint: string;
  strengthGuide: string;
  weakPoint: string;
  weakGuide: string;
};

type MirroringResultPayload = {
  total: number;
  correct: number;
  wrong: number;
  score?: number;
  finished: boolean;
  analysis?: Partial<MirroringAnalysisResult>;
};

type ReviewPracticeQuestion = {
  id: string;
  sourceReviewId?: string;
  sourceTitle: string;
  category: string;
  prompt: string;
  sentence: string;
  choices: string[];
  correctAnswer: string;
  explanation: string;
  sceneKo?: string;
  sceneEn?: string;
  bookImageUrl?: string;
};

type SpeakingAnalysisResult = {
  nativeSimilarity: number;
  pronunciation: number;
  intonation: number;
  stress: number;
  comment: string;
  transcript?: string;
};

type ReviewPracticeState = {
  reviewId: string;
  kind: "reading" | "speaking";
  questions: ReviewPracticeQuestion[];
  index: number;
  answers: Record<string, string>;
  result: { total: number; correct: number; wrong: number; finished: boolean } | null;
  speakingAnalysis?: SpeakingAnalysisResult | null;
};

type ReviewPracticeResultSummary = {
  date: string;
  total: number;
  correct: number;
  wrong: number;
};

type MissionCompletionState = {
  reading?: string;
  speaking?: string;
  mirroring?: Partial<Record<"day" | "week" | "month", string>>;
  mirroringStudyDates?: string[];
  reviewResults?: Partial<Record<"reading" | "speaking", ReviewPracticeResultSummary>>;
};

type DailyMissionStatus = {
  reading: boolean;
  speaking: boolean;
  mirroring: boolean;
  completedCount: number;
  remainingCount: number;
};

const API_BASE = (import.meta.env.VITE_VIBE_API_BASE || "/api/v1").replace(/\/$/, "");
const DEFAULT_KAKAO_REST_API_KEY = "2a38b2b8474ee8b7bae702c35178f00e";
const DEFAULT_KAKAO_REDIRECT_URI = "https://seed2-vibe-a-template.pages.dev/vibe-b/";
const KAKAO_REST_API_KEY = import.meta.env.VITE_KAKAO_REST_API_KEY || DEFAULT_KAKAO_REST_API_KEY;
const KAKAO_REDIRECT_URI = import.meta.env.VITE_KAKAO_REDIRECT_URI || DEFAULT_KAKAO_REDIRECT_URI;
const STORAGE_KEY = "vibeB.currentUser";
const ROUTE_STACK_KEY = "vibeB.routeStack";
const MISSION_STORAGE_PREFIX = "vibeB.missionCompletion";
const DEFAULT_CLASS_NAME = "클래스 미지정";
const DEFAULT_DEMO_BIRTH = "2016-01-01";
const FORM_DELAY_MS = 220;

function getUserStorage() {
  return typeof window === "undefined" ? null : window.sessionStorage;
}

function clearLegacyUserStorage() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage access errors in restricted webview modes.
  }
}

type AuthSessionStatus = {
  checkedAt: string;
  code: boolean;
};

type VibeView =
  | "login"
  | "kakaoCallback"
  | "signupTerms"
  | "signupProfile"
  | "signupEmail"
  | "signupEmailVerify"
  | "signupComplete"
  | "findId"
  | "findIdResult"
  | "findPassword"
  | "findPasswordReset"
  | "home"
  | "noticeList"
  | "noticeDetail"
  | "ticket"
  | "reading"
  | "readingDetail"
  | "speaking"
  | "speakingDetail"
  | "readingPractice"
  | "speakingPractice"
  | "mirroring"
  | "mirroringPractice"
  | "mirroringResult"
  | "mypage"
  | "editProfile"
  | "inquiryList"
  | "inquiryForm"
  | "inquiryDetail";

const NAV_TABS = [
  { id: "home", label: "Home", icon: "is-home" },
  { id: "reading", label: "Reading", icon: "is-reading" },
  { id: "speaking", label: "Speaking", icon: "is-speaking" },
  { id: "mirroring", label: "Mirroring", icon: "is-mirroring" },
  { id: "mypage", label: "My", icon: "is-mypage" },
] as const;

const AUTH_REQUIRED_VIEWS = new Set<VibeView>([
  "home",
  "noticeList",
  "noticeDetail",
  "ticket",
  "reading",
  "readingDetail",
  "speaking",
  "speakingDetail",
  "readingPractice",
  "speakingPractice",
  "mirroring",
  "mirroringPractice",
  "mirroringResult",
  "mypage",
  "editProfile",
  "inquiryList",
  "inquiryForm",
  "inquiryDetail",
]);

const TICKET_REQUIRED_VIEWS = new Set<VibeView>([
  "reading",
  "readingDetail",
  "speaking",
  "speakingDetail",
  "readingPractice",
  "speakingPractice",
  "mirroring",
  "mirroringPractice",
  "mirroringResult",
]);

function isTicketRequiredView(view: VibeView) {
  return TICKET_REQUIRED_VIEWS.has(view);
}

const TERMS = [
  {
    id: "service",
    label: "이용약관 동의",
    required: true,
    content:
      "서비스 이용 목적, 회원의 권리와 의무, 서비스 제공 및 제한, 계정 관리 기준에 관한 일반 약관입니다. 이용자는 본 약관에 동의해야 서비스를 이용할 수 있습니다.",
  },
  {
    id: "privacy",
    label: "개인 정보 수집 및 이용",
    required: true,
    content:
      "회원가입 및 로그인, 문의 응답, 학습 결과 관리에 필요한 최소 개인정보를 처리합니다. 이용자는 동의를 거부할 권리가 있으며, 거부 시 동의가 필요한 기능을 이용할 수 없습니다.",
  },
  {
    id: "outsourcing",
    label: "개인 정보 취급 위탁",
    required: true,
    content:
      "서비스 운영, 본인 확인, 이메일 발송, 고객 문의 처리 등 필요한 업무를 외부 전문 업체에 위탁할 수 있으며, 위탁 업체는 목적 범위 내에서만 정보를 처리합니다.",
  },
  {
    id: "marketing",
    label: "마케팅 정보 활용",
    required: false,
    content:
      "학습법 가이드, 이벤트, 혜택 안내를 수신할 수 있습니다. 수신 동의는 언제든지 변경 가능합니다.",
  },
  {
    id: "location",
    label: "위치 기반 수집 동의",
    required: false,
    content:
      "위치 기반 서비스가 필요한 경우 사용자의 위치 정보를 수집할 수 있습니다. 위치 정보는 서비스 제공 목적 외 용도로 사용하지 않습니다.",
  },
] as const;

type TermId = (typeof TERMS)[number]["id"];
type TermAgreementState = Record<TermId, boolean>;

const INITIAL_TERM_STATE = TERMS.reduce((acc, term) => {
  acc[term.id] = false;
  return acc;
}, {} as TermAgreementState);

const NOTICE_FALLBACK: VibeNotice[] = [
  {
    id: "intro-01",
    title: "서비스 이용 안내",
    description: "스피킹 미러링을 시작할 수 있는 가이드 안내입니다.",
    date: "2026.06.20",
    category: "notice",
    body: [
      "스피킹/리딩/미러링 메뉴는 로그인이 완료된 뒤에 이용 가능합니다.",
      "이용권이 등록되어 있지 않으면 메뉴 진입 전 경고를 안내합니다.",
      "문의/피드백이 필요할 경우 고객지원 게시판을 통해 전달할 수 있습니다.",
    ],
  },
];

const INQUIRY_FALLBACK: VibeInquiry[] = [];

const INQUIRY_FILTERS = ["전체", "서비스 이용 문의", "탈퇴 문의", "환불 문의", "기타 문의"] as const;

function normalizeNoticeCopy(notice: VibeNotice): VibeNotice {
  const preset: Record<string, Partial<VibeNotice>> = {
    "notice-ticket": {
      title: "🎉 새로운 소식과 디자인이 열렸어요!",
      description: "2026.04.24",
      date: "2026.04.24",
      body: [
        "이번 업데이트를 통해 신규 디자인과 사용성을 개선한 화면이 반영되었습니다.",
        "더 편리한 학습 흐름을 위해 홈, 사용등록, 공지사항 화면을 정리했습니다.",
      ],
    },
    "notice-speaking": {
      title: "📚 리딩 콘텐츠가 업데이트되었어요",
      description: "2026.04.24",
      date: "2026.04.24",
      body: ["새로운 리딩 콘텐츠가 추가되었습니다.", "관리자가 배정한 리뷰를 확인하고 학습을 이어가세요."],
    },
    "notice-maintenance": {
      title: "💡 AI 코치 기능이 더 좋아졌어요",
      description: "2026.04.22",
      date: "2026.04.22",
      body: ["AI 코치 기능이 개선되었습니다.", "리딩과 스피킹 오답을 바탕으로 더 자연스러운 복습을 제공합니다."],
    },
  };
  const convert = (value: string) =>
    value
      .replace(/이용권 코드/g, "등록번호")
      .replace(/이용권/g, "등록번호")
      .replace(/등록번호이/g, "등록번호가")
      .replace(/등록번호은/g, "등록번호는");
  const next = { ...notice, ...preset[notice.id] };
  return {
    ...next,
    title: convert(next.title || ""),
    description: convert(next.description || ""),
    body: (next.body || []).map(convert),
  };
}

const DOMAIN_OPTIONS = ["naver.com", "gmail.com", "daum.net", "직접입력"] as const;
type EmailDomainOption = (typeof DOMAIN_OPTIONS)[number];

function composeEmailAddress(emailId: string, domain: EmailDomainOption, customDomain: string) {
  const selectedDomain = domain === "직접입력" ? customDomain.trim() : domain;
  return emailId.trim() && selectedDomain ? `${emailId.trim()}@${selectedDomain}`.toLowerCase() : "";
}

const KIND_LABEL: Record<"reading" | "speaking", string> = {
  reading: "리딩",
  speaking: "스피킹",
};

const MIRRORING_PERIOD_OPTIONS = [
  { id: "day", label: "일" },
  { id: "week", label: "주" },
  { id: "month", label: "월" },
] as const;

const PRACTICE_FALLBACK_CHOICES = ["the key phrase", "the first option", "usually", "sometimes"];

function createLocalMirroringQuestion(period: "day" | "week" | "month"): MirroringQuestion {
  const periodLabel = MIRRORING_PERIOD_OPTIONS.find((option) => option.id === period)?.label || "일";
  return {
    id: `local-mirroring-${period}-${Date.now()}`,
    sourceTitle: `최근 ${periodLabel}간 오답노트`,
    category: "AI 빈칸 채우기",
    prompt: "빈 칸에 들어갈 단어는?",
    sentence: "I ate bulgogi. ______ good.",
    choices: ["It was", "They are", "I am", "We do"],
    correctAnswer: "It was",
    explanation: "앞 문장에서 음식 경험을 말하고 있으므로 뒤 문장은 It was good.이 자연스럽습니다.",
  };
}

class ApiError extends Error {
  status: number;
  payload: ApiEnvelope | null;

  constructor(message: string, status = 0, payload: ApiEnvelope | null = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

function readUserFromStorage(): VibeUser | null {
  clearLegacyUserStorage();
  try {
    const raw = getUserStorage()?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as VibeUser;
    if (!parsed || typeof parsed.id !== "string" || !parsed.email) return null;
    return toVibeUser(parsed) || parsed;
  } catch {
    return null;
  }
}

function normalizeEmail(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function toVibeUser(raw: unknown): VibeUser | null {
  if (!raw || typeof raw !== "object") return null;
  const source = raw as Record<string, unknown>;

  if ("user" in source) {
    const nestedUser = toVibeUser(source.user);
    if (nestedUser) return nestedUser;
  }

  const id = normalizeEmail(source.id);
  const email = normalizeEmail(source.email);
  const name = normalizeEmail(source.name) || id || normalizeEmail(source.username);
  const phone =
    normalizeEmail(source.phone) ||
    normalizeEmail(source.mobile) ||
    normalizeEmail(source.phoneNumber) ||
    normalizeEmail(source.contact);
  const birth =
    normalizeEmail(source.birth) ||
    normalizeEmail(source.birthDate) ||
    normalizeEmail(source.birthday);
  const authToken = normalizeEmail(source.authToken) || normalizeEmail(source.token);
  const authTokenExpiresAt =
    typeof source.authTokenExpiresAt === "number"
      ? source.authTokenExpiresAt
      : typeof source.expiresAt === "number"
        ? source.expiresAt
        : undefined;
  const isDemoProfile = email.endsWith("@demo.local") || email.endsWith("@example.com");

  if (!id || !email) return null;

  return {
    id,
    email,
    name,
    phone,
    gender: normalizeEmail(source.gender) || (isDemoProfile ? "female" : ""),
    className: normalizeEmail(source.className) || normalizeEmail(source.class) || undefined,
    birth: birth || (isDemoProfile ? DEFAULT_DEMO_BIRTH : undefined),
    thumbnailUrl: normalizeEmail(source.thumbnailUrl) || normalizeEmail(source.profileImage) || undefined,
    provider: normalizeEmail(source.provider) || undefined,
    authToken: authToken || undefined,
    authTokenExpiresAt,
  };
}

function getApiEnvelopeUser(result: ApiEnvelope): VibeUser | null {
  if (!result) return null;
  const dataPayload = result.data as { user?: unknown; token?: unknown; expiresAt?: unknown } | undefined;
  const fromDataUser = toVibeUser(dataPayload?.user);
  if (fromDataUser) {
    const token = normalizeEmail(dataPayload?.token) || fromDataUser.authToken;
    const expiresAt = typeof dataPayload?.expiresAt === "number" ? dataPayload.expiresAt : fromDataUser.authTokenExpiresAt;
    return {
      ...fromDataUser,
      authToken: token || undefined,
      authTokenExpiresAt: expiresAt,
    };
  }

  const fromPayload = toVibeUser(result.data);
  if (fromPayload) return fromPayload;

  return toVibeUser((result as ApiEnvelope & { user?: unknown }).user);
}

function needsKakaoProfileCompletion(user: VibeUser) {
  if (user.provider !== "kakao") return false;
  return !user.phone?.trim() || !user.birth?.trim() || !user.gender?.trim();
}

function writeUserToStorage(user: VibeUser | null) {
  clearLegacyUserStorage();
  const storage = getUserStorage();
  if (!storage) return;
  if (user) {
    storage.setItem(STORAGE_KEY, JSON.stringify(user));
    return;
  }
  storage.removeItem(STORAGE_KEY);
}

function toApiUrl(path: string) {
  if (/^https?:\/\//.test(path)) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (normalized.startsWith("/api/v1") || normalized.startsWith("/api/vibe-auth")) return normalized;
  return `${API_BASE}${normalized}`;
}

function parseResponse<T>(response: Response): Promise<ApiEnvelope<T>> {
  return response.text().then((raw) => {
    let payload: ApiEnvelope<T> | null = null;
    try {
      payload = raw ? (JSON.parse(raw) as ApiEnvelope<T>) : null;
    } catch {
      payload = null;
    }

    if (!response.ok || !payload || payload.ok === false) {
      const message = payload?.message || `${response.status} ${response.statusText || "요청 실패"}`;
      throw new ApiError(message, response.status, payload);
    }

    if (!payload || typeof payload.ok !== "boolean" || !payload.message) {
      throw new ApiError("응답 형식이 올바르지 않습니다.", response.status, payload);
    }

    return payload;
  });
}

function readStoredUserAuthForApi() {
  if (typeof window === "undefined") return { userId: "", authToken: "" };
  clearLegacyUserStorage();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { userId: "", authToken: "" };
    const parsed = JSON.parse(raw) as { id?: unknown; email?: unknown; authToken?: unknown; authTokenExpiresAt?: unknown };
    const id = typeof parsed.id === "string" ? parsed.id.trim() : "";
    const email = typeof parsed.email === "string" ? parsed.email.trim() : "";
    const token = typeof parsed.authToken === "string" ? parsed.authToken.trim() : "";
    const expiresAt = typeof parsed.authTokenExpiresAt === "number" ? parsed.authTokenExpiresAt : 0;
    return {
      userId: (id || email).toLowerCase(),
      authToken: token && (!expiresAt || expiresAt > Date.now()) ? token : "",
    };
  } catch {
    return { userId: "", authToken: "" };
  }
}

async function requestApi<T>(path: string, options: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const activeUser = readStoredUserAuthForApi();
  const response = await fetch(toApiUrl(path), {
    method: options.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      ...(activeUser.authToken ? { Authorization: `Bearer ${activeUser.authToken}` } : {}),
      ...(activeUser.userId ? { "x-vibe-user-id": activeUser.userId } : {}),
      ...(options.headers || {}),
    },
    body: options.body,
    credentials: "include",
  });

  return parseResponse<T>(response);
}

function blobToDataUrl(blob: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(String(reader.result || "")));
    reader.addEventListener("error", () => reject(reader.error || new Error("녹음 파일 변환 실패")));
    reader.readAsDataURL(blob);
  });
}

function getPreferredRecordingMimeType() {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") return "";
  return [
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/webm;codecs=opus",
    "audio/webm",
  ].find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || "";
}

function encodeWav(samples: Float32Array, sampleRate: number) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeString = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) view.setUint8(offset + index, value.charCodeAt(index));
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let index = 0; index < samples.length; index += 1, offset += 2) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }

  return new Blob([view], { type: "audio/wav" });
}

async function normalizeRecordingBlob(blob: Blob) {
  const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return { blob, mimeType: blob.type || "audio/webm" };

  const audioContext = new AudioContextClass();
  try {
    const audioBuffer = await audioContext.decodeAudioData(await blob.arrayBuffer());
    const samples = new Float32Array(audioBuffer.length);
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel += 1) {
      const data = audioBuffer.getChannelData(channel);
      for (let index = 0; index < data.length; index += 1) samples[index] += data[index] / audioBuffer.numberOfChannels;
    }
    return { blob: encodeWav(samples, audioBuffer.sampleRate), mimeType: "audio/wav" };
  } catch {
    return { blob, mimeType: blob.type || "audio/webm" };
  } finally {
    void audioContext.close();
  }
}

function getKakaoRedirectUri() {
  if (KAKAO_REDIRECT_URI) return KAKAO_REDIRECT_URI;
  return `${window.location.origin}${window.location.pathname}`;
}

function createKakaoAuthorizeUrl() {
  const redirectUri = getKakaoRedirectUri();
  const state = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  window.sessionStorage.setItem("vibeB.kakaoOAuthState", state);

  const params = new URLSearchParams({
    client_id: KAKAO_REST_API_KEY,
    redirect_uri: redirectUri,
    response_type: "code",
    state,
  });

  return `https://kauth.kakao.com/oauth/authorize?${params.toString()}`;
}

async function requestAuth<T>(action: string, payload: Record<string, unknown>): Promise<ApiEnvelope<T>> {
  return requestApi<T>("/api/vibe-auth", {
    method: "POST",
    body: JSON.stringify({ ...payload, action }),
  });
}

function safeDateValue(value: string) {
  return value || "—";
}

function formatDate(value: string) {
  if (!value) return "미설정";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
}

function toDateInputValue(value: string) {
  if (!value) return "";
  const normalized = value.replace(/[.\/]/g, "-").trim();
  const match = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) return "";
  const [, year, month, day] = match;
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

function getDateKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function getDateKeyByOffset(offsetDays: number) {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return getDateKey(date);
}

function getWeekKey(date = new Date()) {
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = target.getDay() || 7;
  target.setDate(target.getDate() - day + 1);
  return `week-${getDateKey(target)}`;
}

function getMonthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function getMissionCycleKey(period: "day" | "week" | "month") {
  if (period === "week") return getWeekKey();
  if (period === "month") return getMonthKey();
  return getDateKey();
}

function getMissionStorageKey(user: VibeUser | null) {
  const key = (user?.id || user?.email || "guest").trim().toLowerCase();
  return `${MISSION_STORAGE_PREFIX}.${key}`;
}

function readMissionCompletion(user: VibeUser | null): MissionCompletionState {
  if (!user || typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(getMissionStorageKey(user));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as MissionCompletionState;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMissionCompletion(user: VibeUser | null, value: MissionCompletionState) {
  if (!user || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(getMissionStorageKey(user), JSON.stringify(value));
  } catch {
    // Local completion state is a convenience cache; ignore storage quota/private-mode failures.
  }
}

function normalizeMissionDateList(value: unknown) {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value.filter((item): item is string => typeof item === "string" && /^\d{4}-\d{2}-\d{2}$/.test(item)),
    ),
  ).sort((a, b) => b.localeCompare(a));
}

function withMirroringStudyDate(completion: MissionCompletionState) {
  const today = getDateKey();
  return Array.from(new Set([today, ...normalizeMissionDateList(completion.mirroringStudyDates)])).sort((a, b) => b.localeCompare(a)).slice(0, 366);
}

function getMirroringStudyStreak(completion: MissionCompletionState) {
  const dates = new Set(normalizeMissionDateList(completion.mirroringStudyDates));
  if (completion.mirroring?.day && /^\d{4}-\d{2}-\d{2}$/.test(completion.mirroring.day)) {
    dates.add(completion.mirroring.day);
  }

  let streak = 0;
  for (let offset = 0; offset > -366; offset -= 1) {
    if (!dates.has(getDateKeyByOffset(offset))) break;
    streak += 1;
  }
  return streak;
}

function getDailyMissionStatus(completion: MissionCompletionState): DailyMissionStatus {
  const today = getDateKey();
  const reading = completion.reading === today;
  const speaking = completion.speaking === today;
  const mirroring = completion.mirroring?.day === today;
  const completedCount = [reading, speaking, mirroring].filter(Boolean).length;

  return {
    reading,
    speaking,
    mirroring,
    completedCount,
    remainingCount: Math.max(0, 3 - completedCount),
  };
}

function getTodayReviewWrongCount(completion: MissionCompletionState) {
  const today = getDateKey();
  return (["reading", "speaking"] as const).reduce((total, kind) => {
    const result = completion.reviewResults?.[kind];
    if (!result || result.date !== today) return total;
    return total + Math.max(0, result.wrong);
  }, 0);
}

function isReviewMissionCompleted(completion: MissionCompletionState, kind: "reading" | "speaking") {
  return completion[kind] === getDateKey();
}

function isMirroringMissionCompleted(completion: MissionCompletionState, period: "day" | "week" | "month") {
  return completion.mirroring?.[period] === getMissionCycleKey(period);
}

function getMirroringPeriodLabel(period: "day" | "week" | "month") {
  if (period === "week") return "이번 주";
  if (period === "month") return "이번 달";
  return "오늘";
}

function getMirroringNotebookTitle(period: "day" | "week" | "month") {
  if (period === "week") return "주간 오답노트";
  if (period === "month") return "월간 오답노트";
  return "일간 오답노트";
}

function formatKoreanDate(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "미설정";
  return `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, "0")}월 ${String(date.getDate()).padStart(2, "0")}일`;
}

function formatMonthDay(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "오늘";
  return `${String(date.getMonth() + 1).padStart(2, "0")}월 ${String(date.getDate()).padStart(2, "0")}일`;
}

function formatRelativeDate(value: string) {
  const now = Date.now();
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return "";
  const diff = now - target;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "방금 전";
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days}일 전`;
  return formatDate(value);
}

function formatPhone(input: string) {
  const digits = input.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function getUserThumbnailSrc(user: Pick<VibeUser, "name" | "thumbnailUrl">) {
  return user.thumbnailUrl || homeMascotImage;
}

function getUserThumbnailClass(user: Pick<VibeUser, "thumbnailUrl">) {
  return user.thumbnailUrl ? undefined : "is-default-bear";
}

function getUserClassName(user: Pick<VibeUser, "className">) {
  return user.className || DEFAULT_CLASS_NAME;
}

function getMicrophoneErrorMessage(error: unknown) {
  if (typeof window !== "undefined" && !window.isSecureContext) {
    return "마이크는 HTTPS 또는 localhost 환경에서만 사용할 수 있습니다.";
  }

  const name = error instanceof DOMException ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "마이크 권한이 차단되어 있습니다. 브라우저 사이트 설정에서 마이크를 허용해 주세요.";
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "사용 가능한 마이크 장치를 찾지 못했습니다.";
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return "다른 앱에서 마이크를 사용 중입니다. 사용 중인 앱을 종료한 뒤 다시 시도해 주세요.";
  }
  if (name === "AbortError") {
    return "마이크 연결이 중단되었습니다. 다시 녹음해 주세요.";
  }

  return "마이크를 시작하지 못했습니다. 브라우저 권한과 입력 장치를 확인해 주세요.";
}

function pickReviewPracticeQuestions(review: VibeReview): ReviewPracticeQuestion[] {
  const assignedQuestions = (review.questions || []).map((item, index) => {
    const choices = Array.from(
      new Set([
        item.correctAnswer,
        ...(item.choices || []),
        ...PRACTICE_FALLBACK_CHOICES,
      ]),
    )
      .filter(Boolean)
      .slice(0, 4);

    return {
      id: item.id || `assigned-${review.id}-${index}`,
      sourceReviewId: item.sourceReviewId || review.id,
      sourceTitle: item.sourceTitle || review.title,
      category: item.category || (review.kind === "reading" ? "Teacher's Question" : "오늘의 인사"),
      prompt: item.prompt,
      sentence: item.sentence || review.passage || "관리자 배정 지문이 없습니다.",
      correctAnswer: item.correctAnswer,
      explanation: item.explanation || "관리자가 등록한 정답 해설입니다.",
      choices: choices.length >= 2 ? choices : [item.correctAnswer, ...PRACTICE_FALLBACK_CHOICES].slice(0, 4),
      sceneKo: item.sceneKo,
      sceneEn: item.sceneEn,
      bookImageUrl: review.bookImageUrl,
    };
  });

  if (assignedQuestions.length > 0) return assignedQuestions;

  if (review.kind === "reading") return [
    {
      id: `sample-${review.id}-1`,
      sourceTitle: "Brown Bear, Brown Bear What Do You See?",
      category: "Teacher's Question",
      prompt: "What did the purple cat see?",
      sentence: "퍼플캣이 본 건 무엇이지?",
      correctAnswer: "Blue Horse",
      explanation: "Purple Cat looked at the Blue Horse. 보라색 고양이가 본 동물을 떠올려보면 정답은 Blue Horse예요.",
      choices: ["Blue Horse", "Blue Cat", "Teacher", "Brown Bear"],
    },
    {
      id: `sample-${review.id}-2`,
      sourceTitle: "Brown Bear, Brown Bear What Do You See?",
      category: "Teacher's Question",
      prompt: "Who wore glasses in the story?",
      sentence: "이 책에 등장한 안경 쓴 사람이 누구일까요?",
      correctAnswer: "Teacher",
      explanation: "The teacher wore glasses in the story. 장면 속 인물을 다시 확인해보세요.",
      choices: ["Teacher", "Blue Horse", "Purple Cat", "Brown Bear"],
    },
    {
      id: `sample-${review.id}-3`,
      sourceTitle: "Brown Bear, Brown Bear What Do You See?",
      category: "Teacher's Question",
      prompt: "What animal appears first in the story?",
      sentence: "이야기에서 가장 먼저 등장한 동물은 무엇일까요?",
      correctAnswer: "Brown Bear",
      explanation: "The story begins with Brown Bear. 첫 장면의 동물을 떠올려보세요.",
      choices: ["Brown Bear", "Teacher", "Blue Horse", "Goldfish"],
    },
  ];

  return [
    {
      id: `sample-${review.id}-1`,
      sourceTitle: "오늘의 인사",
      category: "오늘의 인사",
      prompt: "How's the weather today?",
      sentence: "It's sunny. I like sunny days!",
      correctAnswer: "It's sunny. I like sunny days!",
      explanation: "문장을 또렷하게 읽는 연습을 해요.",
      choices: ["It's sunny. I like sunny days!", "It's rainy.", "I am fine.", "Good night."],
    },
    {
      id: `sample-${review.id}-2`,
      sourceTitle: "오늘의 주제",
      category: "오늘의 주제",
      prompt: "What is your favorite animal?",
      sentence: "My favorite animal is a bear.",
      correctAnswer: "My favorite animal is a bear.",
      explanation: "문장 끝까지 자연스럽게 이어 말하는 연습을 해요.",
      choices: ["My favorite animal is a bear.", "I go to school.", "It is cold.", "Good morning."],
    },
    {
      id: `sample-${review.id}-3`,
      sourceTitle: "오늘의 아이스브레이킹",
      category: "오늘의 아이스브레이킹",
      prompt: "Tell me one fun thing about today.",
      sentence: "I played with my friends today.",
      correctAnswer: "I played with my friends today.",
      explanation: "오늘 있었던 일을 짧고 자연스럽게 말하는 연습을 해요.",
      choices: ["I played with my friends today.", "I am blue.", "This is my bag.", "See you yesterday."],
    },
  ];
}

const SPEAKING_PRACTICE_TYPE_ORDER = ["인사", "주제", "아이스 브레이킹"];

function getSpeakingPracticeType(review: VibeReview, question?: ReviewPracticeQuestion) {
  const text = [question?.category, review.summary, review.title].filter(Boolean).join(" ");
  const lowerText = text.toLowerCase();
  const compactText = text.replace(/\s+/g, "");
  const compactLowerText = lowerText.replace(/\s+/g, "");
  if (compactText.includes("아이스") || compactText.includes("브레이킹") || compactLowerText.includes("ice")) return "아이스 브레이킹";
  if (text.includes("주제") || lowerText.includes("topic")) return "주제";
  if (text.includes("인사") || lowerText.includes("greeting")) return "인사";
  return question?.category || review.summary || review.title || "스피킹";
}

function createSpeakingPracticeReview(baseReview: VibeReview, reviewList: VibeReview[]) {
  const speakingReviews = reviewList.filter((item) => item.kind === "speaking");
  const sourceReviews = speakingReviews.length ? speakingReviews : [baseReview];
  const grouped = new Map<string, ReviewPracticeQuestion>();

  for (const review of sourceReviews) {
    for (const question of pickReviewPracticeQuestions(review)) {
      const type = getSpeakingPracticeType(review, question);
      if (!grouped.has(type)) grouped.set(type, { ...question, sourceReviewId: question.sourceReviewId || review.id, category: type });
    }
  }

  const orderedQuestions = SPEAKING_PRACTICE_TYPE_ORDER.map((type) => grouped.get(type)).filter(
    (question): question is ReviewPracticeQuestion => Boolean(question),
  );
  const extraQuestions = [...grouped.entries()]
    .filter(([type]) => !SPEAKING_PRACTICE_TYPE_ORDER.includes(type))
    .map(([, question]) => question);
  const questions = [...orderedQuestions, ...extraQuestions].slice(0, 3);

  return {
    ...baseReview,
    title: "Speaking Review",
    summary: questions.map((question) => question.category).join(", ") || baseReview.summary,
    questions: questions.map((question) => ({
      id: question.id,
      sourceReviewId: question.sourceReviewId || baseReview.id,
      sourceTitle: question.sourceTitle,
      category: question.category,
      prompt: question.prompt,
      sentence: question.sentence,
      correctAnswer: question.correctAnswer,
      explanation: question.explanation,
      choices: question.choices,
    })),
  };
}

function calculateReviewPracticeScore(
  questions: ReviewPracticeQuestion[],
  answers: Record<string, string>,
) {
  const total = questions.length;
  let correct = 0;

  for (const question of questions) {
    const userAnswer = (answers[question.id] || "").trim().toLowerCase();
    const correctAnswer = question.correctAnswer.trim().toLowerCase();
    if (userAnswer && userAnswer === correctAnswer) {
      correct += 1;
    }
  }

  return {
    total,
    correct,
    wrong: total - correct,
    finished: true,
  };
}

function readVibeView(): VibeView {
  const search = new URLSearchParams(window.location.search);
  if ((search.get("code") || search.get("error")) && search.get("state")) return "kakaoCallback";
  const routeParams = getRouteParams();
  const value = routeParams.get("view") || "login";
  const allowed: VibeView[] = [
    "login",
    "kakaoCallback",
    "signupTerms",
    "signupProfile",
    "signupEmail",
    "signupEmailVerify",
    "signupComplete",
    "findId",
    "findIdResult",
    "findPassword",
    "findPasswordReset",
    "home",
    "noticeList",
    "noticeDetail",
    "ticket",
    "reading",
    "readingDetail",
    "speaking",
    "speakingDetail",
    "readingPractice",
    "speakingPractice",
    "mirroring",
    "mirroringPractice",
    "mirroringResult",
    "mypage",
    "editProfile",
    "inquiryList",
    "inquiryForm",
    "inquiryDetail",
  ];
  return allowed.includes(value as VibeView) ? (value as VibeView) : "login";
}

function getRouteParams() {
  const hash = window.location.hash;
  if (hash.startsWith("#/")) {
    return new URLSearchParams(hash.slice(2));
  }
  return new URLSearchParams(window.location.search);
}

function getContextParams() {
  const params = getRouteParams();
  return {
    noticeId: params.get("noticeId") || "",
    inquiryId: params.get("inquiryId") || "",
    reviewId: params.get("reviewId") || "",
    period: params.get("period") || "day",
    sessionId: params.get("sessionId") || "",
  };
}

type VibeRouteSnapshot = {
  view: VibeView;
  context: ReturnType<typeof getContextParams>;
};

function getCurrentRouteSnapshot(): VibeRouteSnapshot {
  return {
    view: readVibeView(),
    context: getContextParams(),
  };
}

function createRouteUrl(route: VibeRouteSnapshot) {
  const params = new URLSearchParams();
  params.set("view", route.view);
  const contextKeys: Array<keyof VibeRouteSnapshot["context"]> = ["noticeId", "inquiryId", "reviewId", "period", "sessionId"];

  contextKeys.forEach((key) => {
    const value = route.context[key];
    if (value && !["day"].includes(value)) params.set(key, value);
  });

  if (!["noticeDetail", "readingDetail", "speakingDetail", "readingPractice", "speakingPractice", "inquiryDetail"].includes(route.view)) {
    params.delete("noticeId");
    params.delete("inquiryId");
    params.delete("reviewId");
  }

  return `${window.location.pathname}#/${params.toString()}`;
}

function useVibeRouter() {
  const [view, setView] = useState<VibeView>(readVibeView());
  const [context, setContext] = useState(getContextParams());

  const revealBottomNavigation = useCallback((nextView: VibeView) => {
    const reveal = () => {
      document.querySelectorAll<HTMLElement>(".b-home-nav").forEach((nav) => {
        nav.classList.remove("is-scroll-hidden");
      });
      window.dispatchEvent(new CustomEvent("vibeB:navigation", { detail: { view: nextView } }));
    };

    reveal();
    window.setTimeout(reveal, 0);
    window.setTimeout(reveal, 120);
    window.setTimeout(reveal, 360);
  }, []);

  const navigate = useCallback((next: VibeView, nextContext: Partial<{
    noticeId: string;
    inquiryId: string;
    reviewId: string;
    period: string;
    sessionId: string;
  }> = {}) => {
    const nextRoute: VibeRouteSnapshot = {
      view: next,
      context: {
        noticeId: nextContext.noticeId || "",
        inquiryId: nextContext.inquiryId || "",
        reviewId: nextContext.reviewId || "",
        period: nextContext.period || "day",
        sessionId: nextContext.sessionId || "",
      },
    };

    window.history.pushState({ view: nextRoute.view }, "", createRouteUrl(nextRoute));
    setView(next);
    setContext(getContextParams());
    revealBottomNavigation(next);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [revealBottomNavigation]);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    try {
      sessionStorage.removeItem(ROUTE_STACK_KEY);
    } catch {
      // URL state is the source of truth; stale session route stacks must not affect Android back.
    }

    const syncRouteFromUrl = () => {
      const nextRoute = getCurrentRouteSnapshot();

      setView(nextRoute.view);
      setContext(nextRoute.context);
      revealBottomNavigation(nextRoute.view);
      window.setTimeout(() => {
        revealBottomNavigation(nextRoute.view);
        window.scrollTo({ top: 0, behavior: "auto" });
      }, 0);
    };

    window.addEventListener("popstate", syncRouteFromUrl);
    window.addEventListener("hashchange", syncRouteFromUrl);
    syncRouteFromUrl();
    return () => {
      window.removeEventListener("popstate", syncRouteFromUrl);
      window.removeEventListener("hashchange", syncRouteFromUrl);
    };
  }, [revealBottomNavigation]);

  return { view, context, navigate };
}

function AppTopbar({ title, onBack }: { title: string; onBack?: () => void }) {
  return (
    <div className="b-app-topbar">
      <button onClick={onBack} type="button">
        {onBack ? "‹" : ""}
      </button>
      <strong>{title}</strong>
      <button aria-hidden onClick={() => {}} type="button" />
    </div>
  );
}

function BottomNavigation({
  activeTab,
  onMove,
}: {
  activeTab: VibeView;
  onMove: (view: VibeView) => void;
}) {
  const navRef = useRef<HTMLElement | null>(null);
  const [isScrollHidden, setIsScrollHidden] = useState(false);

  useEffect(() => {
    const showNavigation = () => {
      setIsScrollHidden(false);
      navRef.current?.classList.remove("is-scroll-hidden");
    };

    showNavigation();
    const frameId = window.requestAnimationFrame(showNavigation);
    window.addEventListener("vibeB:navigation", showNavigation);
    window.addEventListener("pageshow", showNavigation);
    window.addEventListener("popstate", showNavigation);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("vibeB:navigation", showNavigation);
      window.removeEventListener("pageshow", showNavigation);
      window.removeEventListener("popstate", showNavigation);
    };
  }, []);

  useEffect(() => {
    const frame = navRef.current?.closest(".b-webview-frame");
    if (!frame) return;

    setIsScrollHidden(false);

    const scrollTargets = Array.from(
      frame.querySelectorAll<HTMLElement>(".b-app-screen, .b-webview-auth-screen"),
    ).filter((target) => target.scrollHeight > target.clientHeight + 4);
    if (!scrollTargets.length) {
      setIsScrollHidden(false);
      return;
    }

    let lastTop = Math.max(...scrollTargets.map((target) => target.scrollTop));
    let settleTimer: number | undefined;
    let hasUserScrollIntent = false;

    const markUserScrollIntent = () => {
      hasUserScrollIntent = true;
    };

    const markKeyboardScrollIntent = (event: KeyboardEvent) => {
      if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " "].includes(event.key)) {
        hasUserScrollIntent = true;
      }
    };

    const showAfterScrollStops = () => {
      if (settleTimer) window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => setIsScrollHidden(false), 220);
    };

    const handleScroll = (event: Event) => {
      const target = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
      if (!target) return;

      const currentTop = Math.max(target.scrollTop, 0);
      const delta = currentTop - lastTop;
      lastTop = currentTop;

      if (!hasUserScrollIntent) {
        setIsScrollHidden(false);
        return;
      }

      if (currentTop <= 4 || delta < -4) {
        setIsScrollHidden(false);
      } else if (delta > 8) {
        setIsScrollHidden(true);
      }

      showAfterScrollStops();
    };

    scrollTargets.forEach((target) => {
      target.addEventListener("scroll", handleScroll, { passive: true });
      target.addEventListener("touchmove", markUserScrollIntent, { passive: true });
      target.addEventListener("wheel", markUserScrollIntent, { passive: true });
    });
    window.addEventListener("keydown", markKeyboardScrollIntent);

    return () => {
      if (settleTimer) window.clearTimeout(settleTimer);
      scrollTargets.forEach((target) => {
        target.removeEventListener("scroll", handleScroll);
        target.removeEventListener("touchmove", markUserScrollIntent);
        target.removeEventListener("wheel", markUserScrollIntent);
      });
      window.removeEventListener("keydown", markKeyboardScrollIntent);
    };
  }, [activeTab]);

  return (
    <nav ref={navRef} className={`b-home-nav${isScrollHidden ? " is-scroll-hidden" : ""}`} aria-label="앱 하단 메뉴">
      {NAV_TABS.map((tab) => (
        <button
          className={activeTab === tab.id ? "is-active" : ""}
          key={tab.id}
          onClick={() => onMove(tab.id as VibeView)}
          type="button"
        >
          <i className={`b-home-nav-icon ${tab.icon}`} aria-hidden />
          <span>{tab.label}</span>
        </button>
      ))}
    </nav>
  );
}

function ScreenMessage({ title, message }: { title: string; message: string }) {
  return (
    <section className="b-home-empty-state">
      <strong>{title}</strong>
      <p>{message}</p>
    </section>
  );
}

function LoginScreen({
  onLogin,
  onKakao,
  onMoveSignup,
  onMoveFindId,
  onMoveFindPassword,
  state,
}: {
  onLogin: (idOrEmail: string, password: string) => Promise<void>;
  onKakao: () => Promise<void>;
  onMoveSignup: () => void;
  onMoveFindId: () => void;
  onMoveFindPassword: () => void;
  state: { busy: boolean; message: string };
}) {
  const [idOrEmail, setIdOrEmail] = useState("");
  const [password, setPassword] = useState("");

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onLogin(idOrEmail.trim(), password);
  };
  const hasError = Boolean(state.message && !state.busy);

  return (
    <section className="b-webview-auth-screen b-login-screen b-figma-login-screen">
      <img className="b-login-logo" src={speakingMirroringLogo} alt="스피킹미러링" />
      <div className="b-figma-login-panel">
        <form className="b-login-form b-figma-login-form" onSubmit={onSubmit}>
          <div className={`b-mobile-field b-figma-login-field ${hasError ? "is-error" : ""}`}>
            <input
              autoComplete="username"
              onChange={(event) => setIdOrEmail(event.target.value)}
              placeholder="아이디를 입력해 주세요."
              type="text"
              value={idOrEmail}
            />
            {hasError ? <em>아이디를 확인해 주세요.</em> : null}
          </div>
          <div className={`b-mobile-field b-figma-login-field ${hasError ? "is-error" : ""}`}>
            <input
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
              placeholder="비밀번호를 입력해 주세요."
              type="password"
              value={password}
            />
            {hasError ? <em>비밀번호를 확인해 주세요.</em> : null}
          </div>
          <button className="b-primary-mobile b-figma-login-submit" disabled={state.busy} type="submit">
            {state.busy ? "로그인 중..." : "로그인"}
          </button>
          <button className="b-kakao-login-button b-figma-kakao-login-button" disabled={state.busy} onClick={() => void onKakao()} type="button">
            <i aria-hidden />
            카카오톡 로그인
          </button>
        </form>
        <div className="b-login-links b-figma-login-links">
          <button onClick={onMoveFindId} type="button">
            아이디 찾기
          </button>
          <button onClick={onMoveFindPassword} type="button">
            비밀번호 찾기
          </button>
          <button onClick={onMoveSignup} type="button">
            회원가입
          </button>
        </div>
      </div>
    </section>
  );
}

function KakaoCallbackScreen({
  busy,
  message,
  onBack,
  onResolve,
}: {
  busy: boolean;
  message: string;
  onBack: () => void;
  onResolve: (code: string, redirectUri: string) => Promise<void>;
}) {
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code") || "";
    const state = params.get("state") || "";
    const error = params.get("error") || "";
    const storedState = window.sessionStorage.getItem("vibeB.kakaoOAuthState") || "";

    if (error) {
      void onResolve("", getKakaoRedirectUri());
      return;
    }

    if (!code || !storedState || state !== storedState) {
      void onResolve("", getKakaoRedirectUri());
      return;
    }

    window.sessionStorage.removeItem("vibeB.kakaoOAuthState");
    void onResolve(code, getKakaoRedirectUri());
  }, [onResolve]);

  return (
    <section className="b-webview-auth-screen b-kakao-callback-screen">
      <AppTopbar title="카카오 로그인" onBack={onBack} />
      <ScreenMessage
        title={busy ? "카카오 로그인 처리 중" : "카카오 로그인"}
        message={message || "잠시만 기다려 주세요."}
      />
    </section>
  );
}

function TermsScreen({
  values,
  details,
  onToggle,
  onAllToggle,
  onOpenDetail,
  onBack,
  onNext,
}: {
  values: TermAgreementState;
  details: TermId | null;
  onToggle: (id: TermId) => void;
  onAllToggle: () => void;
  onOpenDetail: (id: TermId) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const requiredAllChecked = TERMS.filter((term) => term.required).every((term) => values[term.id]);

  return (
    <section className="b-webview-auth-screen b-agreement-screen">
      <AppTopbar title="회원가입" onBack={onBack} />
      <div className="b-agreement-headline">
        <h2>
          <span>서비스 이용을 위해</span>
          <span>이용약관 동의가 필요합니다.</span>
        </h2>
      </div>
      <button className={`b-agree-all ${Object.values(values).every(Boolean) ? "is-checked" : ""}`} onClick={onAllToggle} type="button">
        <i aria-hidden />
        <span>약관에 전체 동의합니다.</span>
      </button>
      <div className="b-term-list">
        {TERMS.map((term) => {
          const checked = values[term.id];
          return (
            <article className={details === term.id ? "is-open" : ""} key={term.id}>
              <button className="b-term-check" onClick={() => onToggle(term.id)} type="button">
                <i aria-hidden className={checked ? "is-checked" : ""} />
                <span>
                  <strong className={term.required ? "is-required" : ""}>[{term.required ? "필수" : "선택"}]</strong>
                  <em>{term.label}</em>
                </span>
              </button>
              <button
                aria-expanded={details === term.id}
                aria-label={`${term.label} 자세히 보기`}
                className="b-term-more"
                onClick={() => onOpenDetail(term.id)}
                type="button"
              />
              {details === term.id ? <p className="b-term-content">{term.content}</p> : null}
            </article>
          );
        })}
      </div>
      <button className="b-bottom-action b-primary-mobile" disabled={!requiredAllChecked} onClick={onNext} type="button">
        다음
      </button>
    </section>
  );
}

function SignupProfileScreen({
  onComplete,
  onBack,
  busyMessage,
}: {
  onBack: () => void;
  onComplete: (payload: SignupProfilePayload) => Promise<void>;
  busyMessage: string;
}) {
  const [id, setId] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("female");
  const [birth, setBirth] = useState("");
  const [idCheck, setIdCheck] = useState<{ checked: boolean; message: string; loading: boolean; available: boolean }>({
    checked: false,
    message: "",
    loading: false,
    available: false,
  });
  const [idValidation, setIdValidation] = useState("");
  const [pwValidation, setPwValidation] = useState("");
  const [pwConfirmValidation, setPwConfirmValidation] = useState("");
  const [birthOpen, setBirthOpen] = useState(false);
  const [calendarYear, setCalendarYear] = useState(1995);
  const [calendarMonth, setCalendarMonth] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: currentYear - 1939 }, (_, index) => currentYear - index);
  }, []);
  const calendarDays = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonth, 1).getDay();
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
    return Array.from({ length: firstDay + daysInMonth }, (_, index) => {
      if (index < firstDay) return null;
      const day = index - firstDay + 1;
      const value = `${calendarYear}-${String(calendarMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      return { day, value };
    });
  }, [calendarMonth, calendarYear]);

  const validateId = useCallback((value: string) => {
    const valid = /^[A-Za-z0-9]{8,20}$/.test(value);
    if (!value) return "";
    return valid ? "" : "ID는 영문/숫자 조합 8~20자로 입력해주세요.";
  }, []);

  const validatePassword = useCallback((value: string) => {
    const valid = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,20}$/.test(value);
    if (!value) return "";
    return valid ? "" : "비밀번호는 영문+숫자 조합 8~20자여야 합니다.";
  }, []);

  const onDatePick = useCallback(() => {
    if (birth) {
      const selected = new Date(`${birth}T00:00:00`);
      if (!Number.isNaN(selected.getTime())) {
        setCalendarYear(selected.getFullYear());
        setCalendarMonth(selected.getMonth());
      }
    }
    setBirthOpen(true);
  }, [birth]);

  const onCheckId = async () => {
    setIdCheck({ checked: false, message: "", loading: true, available: false });
    const error = validateId(id);
    if (error) {
      setIdCheck({ checked: false, message: error, loading: false, available: false });
      return;
    }

    try {
      const result = await requestAuth<{ ok: boolean }>("checkId", { id });
      setIdCheck({
        checked: true,
        available: result.ok,
        loading: false,
        message: result.message,
      });
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "ID 중복 확인을 실패했습니다.";
      setIdCheck({ checked: true, message, loading: false, available: false });
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const idMessage = validateId(id);
    const pwMessage = validatePassword(password);
    const pwConfirmMessage = password === passwordConfirm ? "" : "비밀번호가 일치하지 않습니다.";
    setIdValidation(idMessage);
    setPwValidation(pwMessage);
    setPwConfirmValidation(pwConfirmMessage);

    if (idMessage || pwMessage || pwConfirmMessage) return;
    if (!idCheck.available || !idCheck.checked) {
      setIdValidation("ID 중복 확인이 필요합니다.");
      return;
    }
    if (!name.trim()) return;
    if (!phone.trim()) return;
    if (!birth) return;

    setSubmitting(true);
    try {
      await onComplete({
        id: id.trim(),
        password: password.trim(),
        passwordConfirm,
        name: name.trim(),
        phone: phone.trim(),
        gender,
        birth,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="b-webview-auth-screen b-profile-screen b-signup-profile-screen">
      <AppTopbar title="회원가입" onBack={onBack} />
      <h2>
        <span>회원님의 기본 정보를</span>
        <span>입력해 주세요.</span>
      </h2>
      <form className="b-login-form" onSubmit={handleSubmit}>
        <label className="b-mobile-field">
          <span>아이디</span>
          <div className="b-inline-input">
            <input
              maxLength={20}
              onChange={(event) => {
                const value = event.target.value.replace(/[^A-Za-z0-9]/g, "");
                setId(value);
                setIdValidation("");
                setIdCheck({ checked: false, message: "", loading: false, available: false });
              }}
              placeholder="영문+숫자 조합(8~20)"
              type="text"
              value={id}
            />
            <button
              className="b-soft-mobile"
              disabled={idCheck.loading}
              onClick={() => void onCheckId()}
              type="button"
            >
              {idCheck.loading ? "확인중" : "중복확인"}
            </button>
          </div>
          <em>{idValidation || idCheck.message}</em>
        </label>
        <label className="b-mobile-field">
          <span>비밀번호</span>
          <input
            maxLength={20}
            onChange={(event) => {
              setPassword(event.target.value);
              setPwValidation("");
            }}
            placeholder="영문+숫자, 8~20자"
            type="password"
            value={password}
          />
          <em>{pwValidation}</em>
        </label>
        <label className="b-mobile-field">
          <span>비밀번호 확인</span>
          <input
            maxLength={20}
            onChange={(event) => {
              setPasswordConfirm(event.target.value);
              setPwConfirmValidation("");
            }}
            placeholder="비밀번호 재입력"
            type="password"
            value={passwordConfirm}
          />
          <em>{pwConfirmValidation}</em>
        </label>
        <label className="b-mobile-field">
          <span>이름</span>
          <input onChange={(event) => setName(event.target.value)} placeholder="이름" type="text" value={name} />
        </label>
        <label className="b-mobile-field">
          <span>생년월일</span>
          <div className="b-inline-input">
            <input readOnly value={birth} placeholder="YYYY.MM.DD" />
            <button aria-label="생년월일 선택" className="b-calendar-pick-button" onClick={onDatePick} type="button">
              <i aria-hidden />
            </button>
          </div>
        </label>
        <label className="b-mobile-field">
          <span>성별</span>
          <div className="b-gender-control">
            <div>
              <button
                className={gender === "female" ? "is-selected" : ""}
                onClick={() => setGender("female")}
                type="button"
              >
                여성
              </button>
              <button
                className={gender === "male" ? "is-selected" : ""}
                onClick={() => setGender("male")}
                type="button"
              >
                남성
              </button>
            </div>
          </div>
        </label>
        <label className="b-mobile-field">
          <span>휴대전화 번호</span>
          <input
            inputMode="numeric"
            onChange={(event) => setPhone(formatPhone(event.target.value))}
            placeholder="휴대전화 번호를 숫자만 입력해 주세요."
            type="text"
            value={phone}
          />
        </label>
        <button className="b-primary-mobile" disabled={submitting} type="submit">
          {submitting ? "처리중" : "다음"}
        </button>
      </form>
      {busyMessage ? <p style={{ marginTop: "10px", color: "#6f7688", fontSize: "12px" }}>{busyMessage}</p> : null}
      {birthOpen ? (
        <div className="b-layer-overlay b-calendar-bottom-sheet" role="dialog" aria-modal="true" onClick={() => setBirthOpen(false)}>
          <section className="b-calendar-layer" onClick={(event) => event.stopPropagation()}>
            <div className="b-calendar-sheet-handle" />
            <div className="b-calendar-sheet-title">
              <strong>생년월일 선택</strong>
              <button onClick={() => setBirthOpen(false)} type="button">닫기</button>
            </div>
            <div className="b-calendar-selectors">
              <select aria-label="연도" onChange={(event) => setCalendarYear(Number(event.target.value))} value={calendarYear}>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}년
                  </option>
                ))}
              </select>
              <select aria-label="월" onChange={(event) => setCalendarMonth(Number(event.target.value))} value={calendarMonth}>
                {Array.from({ length: 12 }, (_, index) => (
                  <option key={index} value={index}>
                    {index + 1}월
                  </option>
                ))}
              </select>
            </div>
            <div className="b-calendar-week">
              {["일", "월", "화", "수", "목", "금", "토"].map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>
            <div className="b-calendar-grid">
              {calendarDays.map((day, index) =>
                day ? (
                  <button
                    className={birth === day.value ? "is-selected" : ""}
                    key={day.value}
                    onClick={() => {
                      setBirth(day.value);
                      setBirthOpen(false);
                    }}
                    type="button"
                  >
                    {day.day}
                  </button>
                ) : (
                  <span key={`blank-${index}`} />
                ),
              )}
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}

function SignupEmailInputScreen({
  draft,
  busyMessage,
  onBack,
  onNext,
}: {
  draft: SignupProfilePayload | null;
  busyMessage: string;
  onBack: () => void;
  onNext: (payload: SignupPayload, message: string) => void;
}) {
  const [emailId, setEmailId] = useState("");
  const [emailDomain, setEmailDomain] = useState<EmailDomainOption>(DOMAIN_OPTIONS[0]);
  const [customDomain, setCustomDomain] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const email = composeEmailAddress(emailId, emailDomain, customDomain);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft) {
      setMessage("기본 정보 입력 후 이메일 인증을 진행해주세요.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage("이메일을 정확하게 입력해 주세요.");
      return;
    }

    setSending(true);
    setMessage("");
    try {
      const result = await requestAuth("sendCode", { email, purpose: "signup" });
      const devCode = result.devCode ? ` [테스트: ${String(result.devCode)}]` : "";
      onNext({ ...draft, email }, `${result.message}${devCode}`);
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "인증번호 발송 실패");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="b-webview-auth-screen b-email-screen b-signup-email-input-screen">
      <AppTopbar title="회원가입" onBack={onBack} />
      <h2>
        <span>마지막으로 가입을 위해</span>
        <span>이메일을 인증해 주세요.</span>
      </h2>
      <form className="b-login-form" onSubmit={submit}>
        <label className={`b-mobile-field ${message ? "is-error" : ""}`}>
          <span>이메일</span>
          <div className="b-email-row">
            <input
              maxLength={40}
              onChange={(event) => {
                setEmailId(event.target.value.replace(/\s/g, ""));
                setMessage("");
              }}
              placeholder="아이디"
              type="text"
              value={emailId}
            />
            <em>@</em>
            <select
              onChange={(event) => {
                setEmailDomain(event.target.value as EmailDomainOption);
                if (event.target.value !== "직접입력") {
                  setCustomDomain("");
                }
                setMessage("");
              }}
              value={emailDomain}
            >
              {DOMAIN_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          {emailDomain === "직접입력" ? (
            <input
              className="b-domain-direct-input"
              maxLength={40}
              onChange={(event) => {
                setCustomDomain(event.target.value.replace(/\s/g, ""));
                setMessage("");
              }}
              placeholder="도메인 입력"
              type="text"
              value={customDomain}
            />
          ) : null}
          <em>{message || busyMessage || "* 입력하신 이메일로 인증번호가 발송됩니다."}</em>
        </label>
        <button className="b-primary-mobile" disabled={sending || !draft} type="submit">
          {sending ? "발송중" : "다음"}
        </button>
      </form>
    </section>
  );
}

function SignupEmailVerifyScreen({
  draft,
  busyMessage,
  initialMessage,
  onBack,
  onComplete,
}: {
  draft: SignupPayload | null;
  busyMessage: string;
  initialMessage: string;
  onBack: () => void;
  onComplete: (payload: SignupPayload) => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [message, setMessage] = useState(initialMessage);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const resendCode = async () => {
    if (!draft?.email) {
      setMessage("이메일 입력 후 인증을 진행해주세요.");
      return;
    }
    setSending(true);
    try {
      const result = await requestAuth("sendCode", { email: draft.email, purpose: "signup" });
      const devCode = result.devCode ? ` [테스트: ${String(result.devCode)}]` : "";
      setMessage(`${result.message}${devCode}`);
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "인증번호 재발송 실패");
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!draft) {
      setMessage("이메일 입력 후 인증을 진행해주세요.");
      return;
    }
    if (!code.trim()) {
      setMessage("인증번호를 입력해주세요.");
      return;
    }

    setVerifying(true);
    try {
      await requestAuth("verifyCode", { email: draft.email, purpose: "signup", code: code.trim() });
      setMessage("인증번호가 확인되었습니다. 회원가입을 완료하고 있습니다.");
      await onComplete(draft);
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "인증번호 확인 실패");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <section className="b-webview-auth-screen b-email-screen b-signup-email-verify-screen">
      <AppTopbar title="회원가입" onBack={onBack} />
      <h2>
        <span>인증번호가 전송되었습니다.</span>
        <span>완료해 주세요.</span>
      </h2>
      <form className="b-login-form" onSubmit={verifyCode}>
        <label className={`b-mobile-field ${message.includes("확인") || message.includes("실패") ? "is-error" : ""}`}>
          <span>인증번호</span>
          <div className="b-inline-input">
            <input
              inputMode="numeric"
              maxLength={8}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, ""));
                setMessage(initialMessage);
              }}
              placeholder="인증번호를 입력해 주세요."
              type="text"
              value={code}
            />
            <button className="b-soft-mobile" disabled={sending || !draft} onClick={() => void resendCode()} type="button">
              {sending ? "발송중" : "재발송"}
            </button>
          </div>
          <em>{message || busyMessage}</em>
        </label>
        <button className="b-primary-mobile" disabled={verifying || !draft} type="submit">
          {verifying ? "확인중" : code ? "확인" : "다음"}
        </button>
      </form>
    </section>
  );
}

function SignupCompleteScreen({ name, onLogin }: { name: string; onLogin: () => void }) {
  return (
    <section className="b-webview-auth-screen b-email-screen b-signup-complete-screen">
      <AppTopbar title="회원가입" />
      <h2>
        <span>인증번호가 전송되었습니다.</span>
        <span>인증을 완료해 주세요.</span>
      </h2>
      <div className="b-complete-dim" aria-hidden />
      <section className="b-complete-popup" role="dialog" aria-modal="true">
        <strong>회원가입이 완료되었습니다.</strong>
        <p>
          {name || "회원"}님의 회원가입을 축하합니다.
          <br />
          알차고 실속있는 서비스로
          <br />
          찾아 뵙겠습니다.
        </p>
        <button className="b-primary-mobile b-complete-confirm-button" onClick={onLogin} type="button">
          확인
        </button>
      </section>
    </section>
  );
}

function RecoveryEmailField({
  emailId,
  domain,
  customDomain,
  message,
  onChangeCustomDomain,
  onChangeDomain,
  onChangeEmailId,
}: {
  emailId: string;
  domain: EmailDomainOption;
  customDomain: string;
  message: string;
  onChangeCustomDomain: (value: string) => void;
  onChangeDomain: (value: EmailDomainOption) => void;
  onChangeEmailId: (value: string) => void;
}) {
  return (
    <label className={`b-mobile-field ${message ? "is-error" : ""}`}>
      <span>이메일</span>
      <div className="b-email-row b-recovery-email-row">
        <input
          autoComplete="email"
          maxLength={40}
          onChange={(event) => onChangeEmailId(event.target.value.replace(/\s/g, ""))}
          placeholder="아이디"
          type="text"
          value={emailId}
        />
        <em>@</em>
        <select
          aria-label="이메일 도메인 선택"
          onChange={(event) => onChangeDomain(event.target.value as EmailDomainOption)}
          value={domain}
        >
          {DOMAIN_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      {domain === "직접입력" ? (
        <input
          className="b-domain-direct-input"
          maxLength={40}
          onChange={(event) => onChangeCustomDomain(event.target.value.replace(/\s/g, ""))}
          placeholder="도메인 입력"
          type="text"
          value={customDomain}
        />
      ) : null}
      <em>{message || "* 입력하신 이메일로 인증번호가 발송됩니다."}</em>
    </label>
  );
}

function FindIdScreen({
  onFind,
  onBack,
}: {
  onFind: () => void;
  onBack: () => void;
}) {
  const [step, setStep] = useState<"email" | "verify" | "result">("email");
  const [emailId, setEmailId] = useState("");
  const [emailDomain, setEmailDomain] = useState<EmailDomainOption>(DOMAIN_OPTIONS[0]);
  const [customDomain, setCustomDomain] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [foundId, setFoundId] = useState("");
  const [loading, setLoading] = useState(false);
  const email = composeEmailAddress(emailId, emailDomain, customDomain);

  const changeDomain = (nextDomain: EmailDomainOption) => {
    setEmailDomain(nextDomain);
    if (nextDomain !== "직접입력") setCustomDomain("");
    setMessage("");
  };

  const handleBack = () => {
    if (step === "verify") {
      setStep("email");
      setMessage("");
      return;
    }
    if (step === "result") {
      setStep("verify");
      setMessage("");
      return;
    }
    onBack();
  };

  const sendCode = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage("이메일을 정확하게 입력해 주세요.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      const result = await requestAuth("sendCode", { email, purpose: "findId" });
      const devCode = result.devCode ? ` [테스트: ${String(result.devCode)}]` : "";
      setMessage(`${result.message}${devCode}`);
      setStep("verify");
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "인증번호 발송 실패");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!code.trim()) {
      setMessage("인증번호를 입력해 주세요.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      await requestAuth("verifyCode", { email, purpose: "findId", code: code.trim() });
      const result = await requestAuth<{ id: string }>("findId", { email });
      setFoundId(result.data?.id || (typeof result.id === "string" ? result.id : ""));
      setMessage("");
      setStep("result");
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "인증 실패");
    } finally {
      setLoading(false);
    }
  };

  if (step === "result") {
    return (
      <section className="b-webview-auth-screen b-recovery-flow-screen b-recovery-result-step">
        <AppTopbar title="아이디 찾기" onBack={handleBack} />
        <h2>
          <span>가입하신 아이디를</span>
          <span>확인해 주세요.</span>
        </h2>
        <div className="b-recovery-result-card">
          <span>아이디</span>
          <strong>{foundId || "조회 정보 없음"}</strong>
          <p>{email} 정보로 조회된 결과입니다.</p>
        </div>
        <div className="b-recovery-bottom-stack">
          <button className="b-primary-mobile" onClick={onFind} type="button">
            로그인
          </button>
          <button className="b-outline-mobile" onClick={() => setStep("email")} type="button">
            다시 찾기
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="b-webview-auth-screen b-recovery-flow-screen">
      <AppTopbar title="아이디 찾기" onBack={handleBack} />
      <h2>
        {step === "email" ? (
          <>
            <span>아이디를 찾기 위해</span>
            <span>이메일을 입력해 주세요.</span>
          </>
        ) : (
          <>
            <span>인증번호가 전송되었습니다.</span>
            <span>인증을 완료해 주세요.</span>
          </>
        )}
      </h2>
      {step === "email" ? (
        <form className="b-login-form" onSubmit={sendCode}>
          <RecoveryEmailField
            customDomain={customDomain}
            domain={emailDomain}
            emailId={emailId}
            message={message}
            onChangeCustomDomain={(value) => {
              setCustomDomain(value);
              setMessage("");
            }}
            onChangeDomain={changeDomain}
            onChangeEmailId={(value) => {
              setEmailId(value);
              setMessage("");
            }}
          />
          <button className="b-primary-mobile" disabled={loading} type="submit">
            {loading ? "발송중" : "다음"}
          </button>
        </form>
      ) : (
        <form className="b-login-form" onSubmit={verifyCode}>
          <label className={`b-mobile-field ${message ? "is-error" : ""}`}>
            <span>인증번호</span>
            <div className="b-inline-input">
              <input
                inputMode="numeric"
                maxLength={8}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, ""));
                  setMessage("");
                }}
                placeholder="인증번호를 입력해 주세요."
                type="text"
                value={code}
              />
              <button className="b-soft-mobile" disabled={loading} onClick={() => void sendCode()} type="button">
                재발송
              </button>
            </div>
            <em>{message || `${email}로 전송된 8자리 인증번호를 입력해 주세요.`}</em>
          </label>
          <button className="b-primary-mobile" disabled={loading} type="submit">
            {loading ? "확인중" : "확인"}
          </button>
        </form>
      )}
    </section>
  );
}

function FindPasswordScreen({
  onBack,
  onComplete,
}: {
  onBack: () => void;
  onComplete: () => void;
}) {
  const [step, setStep] = useState<"email" | "verify" | "reset" | "complete">("email");
  const [emailId, setEmailId] = useState("");
  const [emailDomain, setEmailDomain] = useState<EmailDomainOption>(DOMAIN_OPTIONS[0]);
  const [customDomain, setCustomDomain] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const email = composeEmailAddress(emailId, emailDomain, customDomain);

  const changeDomain = (nextDomain: EmailDomainOption) => {
    setEmailDomain(nextDomain);
    if (nextDomain !== "직접입력") setCustomDomain("");
    setMessage("");
  };

  const handleBack = () => {
    if (step === "verify") {
      setStep("email");
      setMessage("");
      return;
    }
    if (step === "reset") {
      setStep("verify");
      setMessage("");
      return;
    }
    onBack();
  };

  const sendCode = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMessage("이메일을 정확하게 입력해 주세요.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      const result = await requestAuth("sendCode", { email, purpose: "resetPassword" });
      const devCode = result.devCode ? ` [테스트: ${String(result.devCode)}]` : "";
      setMessage(`${result.message}${devCode}`);
      setStep("verify");
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "인증번호 발송 실패");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!code.trim()) {
      setMessage("인증번호를 입력해 주세요.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      await requestAuth("verifyCode", { email, purpose: "resetPassword", code: code.trim() });
      setStep("reset");
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "인증번호가 일치하지 않습니다.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,20}$/.test(password)) {
      setMessage("비밀번호는 영문+숫자 조합 8~20자여야 합니다.");
      return;
    }
    if (password !== passwordConfirm) {
      setMessage("비밀번호가 일치하지 않습니다.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      await requestAuth("resetPassword", { email, code: code.trim(), password });
      setStep("complete");
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : "비밀번호 변경 실패");
    } finally {
      setLoading(false);
    }
  };

  if (step === "complete") {
    return (
      <section className="b-webview-auth-screen b-recovery-flow-screen b-recovery-complete-step">
        <AppTopbar title="비밀번호 찾기" />
        <h2>
          <span>새 비밀번호 설정이</span>
          <span>완료되었습니다.</span>
        </h2>
        <div className="b-complete-dim" aria-hidden />
        <section className="b-complete-popup" role="dialog" aria-modal="true">
          <strong>비밀번호가 변경되었습니다.</strong>
          <p>
            새로운 비밀번호로
            <br />
            다시 로그인해 주세요.
          </p>
          <button className="b-primary-mobile b-complete-confirm-button" onClick={onComplete} type="button">
            확인
          </button>
        </section>
      </section>
    );
  }

  return (
    <section className="b-webview-auth-screen b-recovery-flow-screen">
      <AppTopbar title="비밀번호 찾기" onBack={handleBack} />
      <h2>
        {step === "email" ? (
          <>
            <span>비밀번호 재설정을 위해</span>
            <span>이메일을 입력해 주세요.</span>
          </>
        ) : step === "verify" ? (
          <>
            <span>인증번호가 전송되었습니다.</span>
            <span>인증을 완료해 주세요.</span>
          </>
        ) : (
          <>
            <span>새 비밀번호를</span>
            <span>입력해 주세요.</span>
          </>
        )}
      </h2>
      {step === "email" ? (
        <form className="b-login-form" onSubmit={sendCode}>
          <RecoveryEmailField
            customDomain={customDomain}
            domain={emailDomain}
            emailId={emailId}
            message={message}
            onChangeCustomDomain={(value) => {
              setCustomDomain(value);
              setMessage("");
            }}
            onChangeDomain={changeDomain}
            onChangeEmailId={(value) => {
              setEmailId(value);
              setMessage("");
            }}
          />
          <button className="b-primary-mobile" disabled={loading} type="submit">
            {loading ? "발송중" : "다음"}
          </button>
        </form>
      ) : step === "verify" ? (
        <form className="b-login-form" onSubmit={verifyCode}>
          <label className={`b-mobile-field ${message ? "is-error" : ""}`}>
            <span>인증번호</span>
            <div className="b-inline-input">
              <input
                inputMode="numeric"
                maxLength={8}
                onChange={(event) => {
                  setCode(event.target.value.replace(/\D/g, ""));
                  setMessage("");
                }}
                placeholder="인증번호를 입력해 주세요."
                type="text"
                value={code}
              />
              <button className="b-soft-mobile" disabled={loading} onClick={() => void sendCode()} type="button">
                재발송
              </button>
            </div>
            <em>{message || `${email}로 전송된 8자리 인증번호를 입력해 주세요.`}</em>
          </label>
          <button className="b-primary-mobile" disabled={loading} type="submit">
            {loading ? "확인중" : "확인"}
          </button>
        </form>
      ) : (
        <form className="b-login-form" onSubmit={resetPassword}>
          <label className={`b-mobile-field ${message.includes("비밀번호") ? "is-error" : ""}`}>
            <span>새 비밀번호</span>
            <input
              autoComplete="new-password"
              maxLength={20}
              onChange={(event) => {
                setPassword(event.target.value);
                setMessage("");
              }}
              placeholder="영문+숫자, 8~20자"
              type="password"
              value={password}
            />
          </label>
          <label className={`b-mobile-field ${message.includes("일치") ? "is-error" : ""}`}>
            <span>비밀번호 확인</span>
            <input
              autoComplete="new-password"
              maxLength={20}
              onChange={(event) => {
                setPasswordConfirm(event.target.value);
                setMessage("");
              }}
              placeholder="비밀번호 재입력"
              type="password"
              value={passwordConfirm}
            />
            <em>{message || "비밀번호는 8~20자 영문+숫자 조합으로 입력해 주세요."}</em>
          </label>
          <button className="b-primary-mobile" disabled={loading} type="submit">
            {loading ? "변경중" : "비밀번호 변경"}
          </button>
        </form>
      )}
    </section>
  );
}

function HomeScreen({
  user,
  ticket,
  notices,
  recentLearning,
  missionStatus,
  reviewWrongCount,
  onOpenNotice,
  onOpenTicket,
  onNavigateProtected,
  onOpenMypage,
}: {
  user: VibeUser;
  ticket: VibeTicket | null;
  notices: VibeNotice[];
  recentLearning: RecentLearningItem[];
  missionStatus: DailyMissionStatus;
  reviewWrongCount: number;
  onOpenNotice: () => void;
  onOpenTicket: () => void;
  onNavigateProtected: (target: "reading" | "speaking" | "mirroring") => void;
  onOpenMypage: () => void;
}) {
  const hasTicket = Boolean(ticket && !ticket.isExpired);
  const displayName = user.name || user.id || "회원";
  const studyStampStates = [
    { id: "reading", image: studyStampOneImage, isDone: missionStatus.reading },
    { id: "speaking", image: studyStampTwoImage, isDone: missionStatus.speaking },
    { id: "mirroring", image: studyStampOneImage, isDone: missionStatus.mirroring },
  ];
  const completedStampCount = missionStatus.completedCount;
  const todayStudyLabel = formatMonthDay(new Date());

  return (
    <section className={`b-app-screen b-home-screen ${hasTicket ? "has-ticket" : "has-no-ticket"}`}>
      <div className="b-home-header">
        <span />
        <strong className="b-home-logo-text">스피킹미러링</strong>
        <button onClick={onOpenNotice} className="b-notice-bell-button" type="button" aria-label="공지사항 보기">
          <i className="b-home-notice-bell" aria-hidden />
          <span>공지사항</span>
        </button>
      </div>

      <section className="b-home-greeting-card">
        <figure className="b-home-mascot" aria-hidden>
          <img src={homeMascotImage} alt="" />
        </figure>
        <h2>{`안녕, ${displayName}!`}</h2>
        <p>오늘도 스피킹 미러링으로 영어랑 놀아볼까?</p>
      </section>

      <section className="b-home-today-study">
        <div className="b-home-section-title">
          <h3>오늘의 학습</h3>
          {hasTicket ? <span>{`진행률 ${completedStampCount}/3`}</span> : null}
        </div>
        {hasTicket ? (
          <article className="b-home-stamp-card">
            <div>
              <strong>{`${todayStudyLabel} · ${getUserClassName(user)}`}</strong>
              <span>{`진행률 ${completedStampCount}/3`}</span>
            </div>
            <div className="b-home-stamps" aria-label={`완료 도장 ${completedStampCount}개`}>
              {studyStampStates.map((stamp) => (
                <span
                  key={stamp.id}
                  className={stamp.isDone ? "is-stamped" : "is-empty"}
                  aria-hidden
                >
                  <img src={stamp.isDone ? stamp.image : studyStampThreeImage} alt="" />
                </span>
              ))}
            </div>
          </article>
        ) : (
          <article className="b-home-empty-course-card">
            <i aria-hidden />
            <strong>아직 등록되지 않은 과정이에요</strong>
            <p>사용 등록 후 확인해 주세요.</p>
            <button onClick={onOpenTicket} type="button">
              사용 등록하기
            </button>
          </article>
        )}
      </section>

      <section className="b-home-mission-section">
        <h3>오늘의 미션</h3>
        <div className="b-home-mission-list">
          <article>
            <img className="b-home-mission-icon" src={missionReadingImage} alt="" />
            <div>
              <strong>Reading 학습<br />3번 해보기</strong>
              <p>{missionStatus.reading ? 3 : 0} / 3 완료</p>
            </div>
            <button onClick={() => onNavigateProtected("reading")} type="button">바로가기</button>
          </article>
          <article>
            <img className="b-home-mission-icon" src={missionSpeakingImage} alt="" />
            <div>
              <strong>Speaking 학습<br />3번 해보기</strong>
              <p>{missionStatus.speaking ? 3 : 0} / 3 진행 중</p>
            </div>
            <button onClick={() => onNavigateProtected("speaking")} type="button">바로가기</button>
          </article>
          <article>
            <img className="b-home-mission-icon" src={missionAiImage} alt="" />
            <div>
              <strong>AI 코치 테디와<br />함께 오답노트</strong>
              <p>{`전체 ${reviewWrongCount}개의 오답이 있어요!`}</p>
            </div>
            <button onClick={() => onNavigateProtected("mirroring")} type="button">바로가기</button>
          </article>
        </div>
      </section>

      <section className="b-home-company-info">
        <strong>주식회사 씨드투</strong>
        <p>대표자 : 윤성필</p>
        <p>주소 : 서울특별시 서초구 신반포로49길 3, 3층 유인어스</p>
        <p>사업자등록번호 : 254-86-02083</p>
        <p>고객센터 : 1877-4287</p>
        <p>고객센터 운영 : 평일 10:00 ~ 17:00(주말, 공휴일 제외)</p>
      </section>

    </section>
  );
}

function NoticeListScreen({
  notices,
  onOpen,
  onBack,
}: {
  notices: VibeNotice[];
  onOpen: (id: string) => void;
  onBack: () => void;
}) {
  return (
    <section className="b-app-screen b-notice-route-screen">
      <AppTopbar title="공지사항" onBack={onBack} />
      <section className="b-notice-list">
        {notices.length === 0 ? (
          <ScreenMessage title="공지사항이 없습니다." message="새로운 공지가 등록되면 여기에서 확인할 수 있습니다." />
        ) : (
          notices.map((notice) => (
            <article key={notice.id} onClick={() => onOpen(notice.id)} role="button" tabIndex={0}>
              <strong>{notice.title}</strong>
              <p>{notice.date}</p>
            </article>
          ))
        )}
      </section>
    </section>
  );
}

function NoticeDetailScreen({
  notice,
  onBack,
}: {
  notice: VibeNotice;
  onBack: () => void;
}) {
  return (
    <section className="b-app-screen b-home-detail-screen b-notice-detail-screen">
      <AppTopbar title="공지사항 상세" onBack={onBack} />
      <h2>{notice.title}</h2>
      <span>{notice.date}</span>
      <section className="b-home-detail-body">
        {notice.body.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </section>
    </section>
  );
}

function TicketScreen({
  onBack,
  onComplete,
  onSubmit,
  loading,
  message,
}: {
  onBack: () => void;
  onComplete: () => void;
  onSubmit: (code: string) => Promise<TicketSubmitResult>;
  loading: boolean;
  message: string;
}) {
  const [code, setCode] = useState("");
  const [popup, setPopup] = useState<"success" | "fail" | null>(null);
  const [localMessage, setLocalMessage] = useState("");
  const [registeredTicket, setRegisteredTicket] = useState<VibeTicket | null>(null);
  const hasCode = code.trim().length > 0;
  const ticketDateRange = () => {
    if (!registeredTicket) return "2026.04.28 - 2026.05.28";
    return `${formatDate(registeredTicket.registeredAt)} - ${formatDate(registeredTicket.expiresAt)}`;
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalized = code.trim().toUpperCase();
    if (!normalized) {
      setLocalMessage("등록번호를 입력해 주세요.");
      return;
    }

    const result = await onSubmit(normalized);
    setLocalMessage(result.ok ? result.message : "");
    setRegisteredTicket(result.ticket || null);
    setPopup(result.ok ? "success" : "fail");
  };

  return (
    <section className={`b-app-screen b-code-register-screen ${hasCode ? "has-code" : ""} ${popup ? "has-modal" : ""}`}>
      <AppTopbar title="사용등록" onBack={onBack} />
      <h2>
        <em>필수 사용등록</em>
        <span>플랫폼비 결제 시 안내 받으신</span>
        <span>이용 등록번호를 입력해 주세요.</span>
      </h2>
      <form className="b-login-form" onSubmit={submit}>
        <label className="b-mobile-field">
          <span>이용 등록번호</span>
          <input
            maxLength={40}
            onChange={(event) => {
              setCode(event.target.value.toUpperCase());
              setLocalMessage("");
            }}
            placeholder="등록번호를 입력해 주세요."
            value={code}
          />
          <em>{popup ? "" : localMessage}</em>
        </label>
        <button className="b-primary-mobile b-fixed-bottom-button" disabled={loading || !hasCode} type="submit">
          {loading ? "등록 중..." : "등록하기"}
        </button>
      </form>
      {popup ? (
        <>
          <div className="b-complete-dim" aria-hidden />
          <section className="b-complete-popup b-ticket-popup" role="dialog" aria-modal="true">
            <strong>{popup === "success" ? "이용 등록이 완료되었습니다." : "이용 등록번호가 일치하지 않습니다."}</strong>
            <p>
              {popup === "success" ? (
                <>
                  사용기간
                  <br />
                  {ticketDateRange()}
                </>
              ) : (
                <>
                  등록번호를 다시 확인해 주세요.
                </>
              )}
            </p>
            <button
              className="b-primary-mobile b-complete-confirm-button"
              onClick={() => {
                if (popup === "success") {
                  onComplete();
                  return;
                }
                setLocalMessage("");
                setPopup(null);
              }}
              type="button"
            >
              {popup === "success" ? "홈으로" : "확인"}
            </button>
          </section>
        </>
      ) : null}
    </section>
  );
}

function ReviewListScreen({
  kind,
  loading,
  error,
  data,
  user,
  onStartPractice,
  onBack,
}: {
  kind: "reading" | "speaking";
  loading: boolean;
  error: string;
  data: VibeReview[];
  user: VibeUser;
  onStartPractice: (review: VibeReview) => void;
  onBack: () => void;
}) {
  const readyReview = data.find((item) => item.status === "new") || data[0] || null;
  const isReady = Boolean(readyReview && !loading && !error);
  const reviewLabel = kind === "reading" ? "Reading Review" : "Speaking Review";
  const readyTitle = kind === "reading" ? "오늘의 리딩 복습이\n준비됐어요!" : "오늘의 스피킹 복습이\n준비됐어요!";
  const preparingTitle = "아직 학습을\n준비하고 있어요";
  const mascot = isReady
    ? kind === "reading"
      ? readingReadyBearImage
      : speakingReadyBearImage
    : kind === "reading"
      ? readingPreparingBearImage
      : speakingPreparingBearImage;
  const dateText = formatKoreanDate(new Date());

  return (
    <section className={`b-app-screen b-review-screen b-review-home-screen is-${kind}`}>
      <AppTopbar title={reviewLabel} onBack={onBack} />
      <section className="b-review-profile-card">
        <img
          alt=""
          className={getUserThumbnailClass(user)}
          src={getUserThumbnailSrc(user)}
        />
        <div>
          <strong>{user.name || user.id || "회원"}님</strong>
          <p>{getUserClassName(user)} · {dateText}</p>
        </div>
      </section>

      <section className="b-review-ready-card">
        <span>Today {reviewLabel}</span>
        <h2>{(loading ? "학습 정보를\n불러오고 있어요" : error ? "학습 정보를\n확인하지 못했어요" : isReady ? readyTitle : preparingTitle).split("\n").map((line) => (
          <FragmentLine key={line}>{line}</FragmentLine>
        ))}</h2>
        <img alt="" src={mascot} />
        <p>{isReady ? "같이 시작해볼까요?" : "잠시만 기다려주세요!"}</p>
        <button
          disabled={!isReady}
          onClick={() => {
            if (readyReview) onStartPractice(readyReview);
          }}
          type="button"
        >
          시작하기
        </button>
      </section>
    </section>
  );
}

function FragmentLine({ children }: { children: string }) {
  return <span>{children}</span>;
}

function ReviewDetailScreen({
  review,
  onBack,
  onMarkReviewed,
  marking,
  onStartPractice,
}: {
  review: VibeReview;
  onBack: () => void;
  onMarkReviewed: () => Promise<void>;
  marking: boolean;
  onStartPractice: () => void;
}) {
  const isReviewed = review.status === "reviewed";
  return (
    <section className="b-app-screen b-home-detail-screen">
      <AppTopbar title={`${KIND_LABEL[review.kind]} 상세`} onBack={onBack} />
      <section className="b-home-detail-screen">
        <h2>{review.title}</h2>
        <span>관리자: {review.adminName}</span>
        <section className="b-home-detail-body">
          <p>
            <strong>요약</strong>
            <br />
            {review.summary}
          </p>
          <p>
            <strong>원문</strong>
            <br />
            {review.passage}
          </p>
          <p>
            <strong>코치 코멘트</strong>
            <br />
            {review.coachComment}
          </p>
          <section className="b-feedback-note">
            <h3>오답 항목</h3>
            {review.wrongItems.length === 0 ? (
              <p>등록된 오답 항목이 없습니다.</p>
            ) : (
              <ul>
                {review.wrongItems.map((item) => (
                  <li key={item.id} style={{ marginBottom: "10px" }}>
                    <strong>{item.sourceTitle}</strong>
                    <p style={{ margin: "4px 0" }}>{item.prompt}</p>
                    <p style={{ margin: "4px 0", color: "#9aa1ae" }}>
                      오답: {item.userAnswer} / 정답: {item.correctAnswer}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <button
            className="b-primary-mobile"
            disabled={review.wrongItems.length === 0}
            onClick={() => void onStartPractice()}
            type="button"
          >
            {review.wrongItems.length === 0 ? "복습할 오답이 없습니다" : "오답 복습 시작"}
          </button>
          <button
            className="b-outline-mobile"
            disabled={isReviewed || marking}
            onClick={() => void onMarkReviewed()}
            type="button"
          >
            {isReviewed ? "복습완료 처리됨" : marking ? "처리중..." : "복습 완료 처리"}
          </button>
        </section>
      </section>
    </section>
  );
}

function ReviewPracticeScreen({
  title,
  reviewId,
  reviewTitle,
  user,
  question,
  index,
  total,
  result,
  speakingAnalysis,
  onBack,
  onSelect,
  onFinish,
}: {
  title: "리딩" | "스피킹";
  reviewId: string;
  reviewTitle: string;
  user: VibeUser;
  question: ReviewPracticeQuestion | null;
  index: number;
  total: number;
  result: { total: number; correct: number; wrong: number; finished: boolean } | null;
  speakingAnalysis?: SpeakingAnalysisResult | null;
  onBack: () => void;
  onSelect: (answer: string, speakingAnalysis?: SpeakingAnalysisResult | null) => Promise<void>;
  onFinish: () => void;
}) {
  const [answer, setAnswer] = useState("");
  const [readingFeedback, setReadingFeedback] = useState<null | { answer: string; isCorrect: boolean }>(null);
  const [speakingStep, setSpeakingStep] = useState<"idle" | "recording" | "recorded" | "submit">("idle");
  const [recordedAudio, setRecordedAudio] = useState<null | { blob: Blob; durationMs: number; mimeType: string }>(null);
  const [recordingError, setRecordingError] = useState("");
  const [savingRecording, setSavingRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingStartedAtRef = useRef(0);
  const savingRecordingRef = useRef(false);
  const isReading = title === "리딩";
  const displayName = user.name || user.id || "회원";

  useEffect(() => {
    setAnswer("");
    setReadingFeedback(null);
    setSpeakingStep("idle");
    setRecordedAudio(null);
    setRecordingError("");
    savingRecordingRef.current = false;
  }, [question?.id]);

  useEffect(() => {
    return () => {
      mediaRecorderRef.current?.state === "recording" && mediaRecorderRef.current.stop();
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  if (!question && !result) {
    return (
      <section className="b-app-screen b-practice-screen b-admin-practice-screen">
        <AppTopbar title={`${title} 학습`} onBack={onBack} />
        <ScreenMessage title="문제를 불러오는 중" message="잠시만 기다려주세요." />
      </section>
    );
  }

  if (result && result.finished) {
    if (!isReading) {
      const analysis = speakingAnalysis || {
        nativeSimilarity: 0,
        pronunciation: 0,
        intonation: 0,
        stress: 0,
        comment: "저장된 음성 녹음 파일을 기준으로 분석 정보가 제공됩니다.",
      };
      const nativeSimilarity = Math.round(Number(analysis.nativeSimilarity) || 0);
      const pronunciation = Math.round(Number(analysis.pronunciation) || 0);
      const intonation = Math.round(Number(analysis.intonation) || 0);
      const stress = Math.round(Number(analysis.stress) || 0);
      const totalScore = Math.round((nativeSimilarity + pronunciation + intonation + stress) / 4);
      return (
        <section className="b-app-screen b-speaking-analysis-screen">
          <AppTopbar title="결과" onBack={onBack} />
          <section className="b-speaking-score-section">
            <span>내 점수는..</span>
            <div className="b-speaking-score-gauge" aria-label={`총점 ${totalScore}점`}>
              <strong>{totalScore}</strong>
              <small>점</small>
              <i><b style={{ width: `${totalScore}%` }} /></i>
            </div>
            <em>{`총점 ${totalScore}점 · 원어민 유사도 ${nativeSimilarity}%`}</em>
          </section>
          <section className="b-speaking-llm-summary">
            <strong>{`${displayName}님의 녹음 분석이 완료되었어요.`}</strong>
            <p>{analysis.comment}</p>
            {analysis.transcript ? <p>{`전사 결과: ${analysis.transcript}`}</p> : null}
          </section>
          <section className="b-speaking-analysis-card">
            <article>
              <strong>발음 정확도 <b>{pronunciation}점</b></strong>
              <p>{pronunciation >= 80 ? "단어를 또렷하게 말했어요. 지금 흐름을 유지해도 좋아요." : "자음과 모음의 시작 소리를 조금 더 분명하게 말해보세요."}</p>
            </article>
            <article>
              <strong>억양 유사도 <b>{intonation}점</b></strong>
              <p>{intonation >= 80 ? "문장 흐름이 자연스럽게 이어졌어요." : "문장 끝을 조금 더 자연스럽게 올리거나 내려 말해보세요."}</p>
            </article>
            <article>
              <strong>강세 표현 <b>{stress}점</b></strong>
              <p>{stress >= 80 ? "중요한 단어의 강세 표현이 안정적이에요." : "핵심 단어를 조금 더 크게, 길게 말하면 더 자연스러워요."}</p>
            </article>
          </section>
          <section className="b-ai-coach-note">
            <strong>AI 코치 테디의 한마디</strong>
            <p>{analysis.comment}</p>
          </section>
          <button className="b-primary-mobile b-fixed-bottom-button" onClick={onFinish} type="button">
            홈으로
          </button>
        </section>
      );
    }

    return (
      <section className="b-app-screen b-reading-complete-screen">
        <AppTopbar title="정답 결과" onBack={onBack} />
        <ProgressDots index={Math.min(index + 1, total)} total={Math.max(total, 3)} />
        <section className="b-reading-book-card">
          <img alt="" src={question?.bookImageUrl || readingBookCoverImage} />
          <div>
            <span>오늘 읽은 책</span>
            <strong>{question?.sourceTitle || reviewTitle || "Brown Bear, Brown Bear What Do You See?"}</strong>
          </div>
        </section>
        <section className="b-reading-complete-card">
          <strong>리딩 학습을 완료했어요!</strong>
          <p>{`${displayName}님, 총 ${result.total}문제 중 ${result.correct}문제를 맞췄어요.`}</p>
          <p>{`정답 ${result.correct}개 · 오답 ${result.wrong}개`}</p>
          <p>오답은 미러링에서 AI 빈칸채우기 문제로 다시 만나볼 수 있어요.</p>
        </section>
        <button className="b-primary-mobile b-fixed-bottom-button" onClick={onFinish} type="button">
          홈으로
        </button>
      </section>
    );
  }

  const submitReadingResult = () => {
    if (!question || !answer.trim()) return;
    const isCorrect = answer.trim().toLowerCase() === question.correctAnswer.trim().toLowerCase();
    setReadingFeedback({ answer: answer.trim(), isCorrect });
  };

  const submitUnknownReadingResult = () => {
    if (!question) return;
    setReadingFeedback({ answer: "잘 모르겠어요", isCorrect: false });
  };

  const goNextFromReadingFeedback = async () => {
    if (!readingFeedback) return;
    await onSelect(readingFeedback.answer);
  };

  const stopSpeakingRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  const startSpeakingRecording = async () => {
    setRecordingError("");
    setRecordedAudio(null);

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setRecordingError("이 브라우저에서는 음성 녹음을 사용할 수 없습니다.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredMimeType = getPreferredRecordingMimeType();
      const recorder = new MediaRecorder(stream, preferredMimeType ? { mimeType: preferredMimeType } : undefined);
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      recordingChunksRef.current = [];
      recordingStartedAtRef.current = Date.now();

      recorder.addEventListener("dataavailable", (event) => {
        if (event.data.size > 0) recordingChunksRef.current.push(event.data);
      });
      recorder.addEventListener("stop", () => {
        const mimeType = recorder.mimeType || preferredMimeType || "audio/webm";
        const blob = new Blob(recordingChunksRef.current, { type: mimeType });
        const durationMs = Math.max(0, Date.now() - recordingStartedAtRef.current);
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        void normalizeRecordingBlob(blob).then((normalized) => {
          setRecordedAudio({
            blob: normalized.blob,
            durationMs,
            mimeType: normalized.mimeType,
          });
          setSpeakingStep("recorded");
        });
      });

      recorder.start();
      setSpeakingStep("recording");
    } catch (error) {
      console.warn("Microphone recording failed", error);
      setRecordingError(getMicrophoneErrorMessage(error));
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
      mediaRecorderRef.current = null;
      setSpeakingStep("idle");
    }
  };

  const submitSpeakingAnswer = async () => {
    if (!question) return;
    if (savingRecordingRef.current) return;
    if (!recordedAudio) {
      setRecordingError("녹음 완료 후 제출해 주세요.");
      setSpeakingStep("recorded");
      return;
    }

    savingRecordingRef.current = true;
    setSavingRecording(true);
    let analysis: SpeakingAnalysisResult | null = null;
    try {
      const audioData = await blobToDataUrl(recordedAudio.blob);
      const saved = await requestApi<{ analysis?: SpeakingAnalysisResult }>("/api/v1/speaking-recordings", {
        method: "POST",
        body: JSON.stringify({
          reviewId,
          questionId: question.id,
          mimeType: recordedAudio.mimeType,
          durationMs: recordedAudio.durationMs,
          audioData,
        }),
      });
      analysis = saved.data?.analysis || null;
    } catch {
      setRecordingError("녹음 파일 저장에 실패했습니다. 다시 시도해 주세요.");
      savingRecordingRef.current = false;
      return;
    } finally {
      setSavingRecording(false);
    }

    if (analysis) setRecordingError("");
    await onSelect(question.correctAnswer || question.sentence, analysis);
    savingRecordingRef.current = false;
  };

  if (!question) return null;

  if (!isReading) {
    return (
      <section className={`b-app-screen b-speaking-practice-screen is-${speakingStep}`}>
        <AppTopbar title="" onBack={onBack} />
        <ProgressDots index={index + 1} total={Math.max(total, 3)} />
        <section className="b-speaking-question-card">
          <span>{question.category || "오늘의 인사"}</span>
          <h2>{question.prompt || "How's the weather today?"}</h2>
          <img alt="" src={speakingQuestionBearImage} />
        </section>
        <section className="b-speaking-script-card">
          <span>이렇게 말해봐요!</span>
          <strong>{question.sentence || "It's sunny. I like sunny days!"}</strong>
        </section>
        {recordingError ? <p className="b-speaking-recording-error">{recordingError}</p> : null}
        {speakingStep === "idle" ? (
          <button className="b-record-button" onClick={() => void startSpeakingRecording()} type="button" aria-label="녹음 시작">
            <i />
          </button>
        ) : speakingStep === "recording" ? (
          <button className="b-record-button is-recording" onClick={stopSpeakingRecording} type="button" aria-label="녹음 종료">
            <i />
          </button>
        ) : speakingStep === "recorded" ? (
          <button className="b-record-complete-button" onClick={() => setSpeakingStep("submit")} type="button" aria-label="녹음 완료">
            ✓
          </button>
        ) : null}
        {speakingStep === "submit" ? (
          <>
            <div className="b-complete-dim" aria-hidden />
            <section className="b-speaking-submit-sheet" role="dialog" aria-modal="true">
              <strong>정말 잘했어요!</strong>
              <p>
                {index + 1 >= total
                  ? "오늘의 스피킹 녹음을 완료했어요. 결과를 확인해볼까요?"
                  : "오늘의 인사를 완료했어요. 다음 문제로 넘어가볼까요?"}
              </p>
              <button
                className="b-primary-mobile b-fixed-bottom-button"
                disabled={savingRecording}
                onClick={() => void submitSpeakingAnswer()}
                type="button"
              >
                {savingRecording ? "저장중" : index + 1 >= total ? "결과 확인하기" : "다음"}
              </button>
            </section>
          </>
        ) : null}
      </section>
    );
  }

  if (readingFeedback) {
    return (
      <section className={`b-app-screen b-reading-result-screen ${readingFeedback.isCorrect ? "is-correct" : "is-wrong"}`}>
        <AppTopbar title="정답 결과" onBack={onBack} />
        <img className="b-reading-result-bear" alt="" src={readingFeedback.isCorrect ? readingResultCorrectImage : readingResultWrongImage} />
        <h2>{readingFeedback.isCorrect ? "정답이에요!" : "아쉬워요..."}</h2>
        <p>{readingFeedback.isCorrect ? `Well done, ${displayName}` : `Just do it, ${displayName}`}</p>
        <section className="b-reading-answer-card">
          <span>내가 쓴 답</span>
          <strong>{readingFeedback.answer}</strong>
          <span>정답</span>
          <strong className="is-answer">{question.correctAnswer}</strong>
        </section>
        <section className="b-ai-coach-note">
          <strong>코치 테디의 해설</strong>
          {question.sceneKo || question.sceneEn ? (
            <>
              {question.sceneKo ? <p>{question.sceneKo}</p> : null}
              {question.sceneEn ? <p>{question.sceneEn}</p> : null}
            </>
          ) : (
            <p>{question.explanation}</p>
          )}
        </section>
        <button className="b-primary-mobile b-fixed-bottom-button" onClick={() => void goNextFromReadingFeedback()} type="button">
          다음
        </button>
      </section>
    );
  }

  return (
    <section className="b-app-screen b-reading-practice-screen">
      <AppTopbar title="" onBack={onBack} />
      <ProgressDots index={index + 1} total={Math.max(total, 3)} />
      <section className="b-reading-book-card">
        <img alt="" src={question.bookImageUrl || readingBookCoverImage} />
        <div>
          <span>오늘 읽은 책</span>
          <strong>{question.sourceTitle || reviewTitle || "Brown Bear, Brown Bear What Do You See?"}</strong>
        </div>
      </section>
      <section className="b-reading-question-card">
        <div>
          <span>{question.category || "Teacher's Question"}</span>
          <strong>{question.prompt || "What did the purple cat see?"}</strong>
          <p>{question.sentence || "퍼플캣이 본 건 무엇이지?"}</p>
        </div>
        <label>
          <span>My Answer</span>
          <input
            onChange={(event) => setAnswer(event.target.value)}
            placeholder="정답을 입력해 주세요."
            value={answer}
          />
        </label>
      </section>
      <button className="b-reading-hint-button" onClick={submitUnknownReadingResult} type="button">잘 모르겠어요 &gt;</button>
      <button className="b-primary-mobile b-fixed-bottom-button" disabled={!answer.trim()} onClick={submitReadingResult} type="button">
        정답 확인하기
      </button>
    </section>
  );
}

function ProgressDots({ index, total }: { index: number; total: number }) {
  const count = Math.max(total, 3);
  return (
    <div className="b-study-progress-dots" aria-label={`문제 ${index} / ${count}`}>
      {Array.from({ length: count }).map((_, dotIndex) => (
        <span key={dotIndex} className={dotIndex + 1 === index ? "is-active" : dotIndex + 1 < index ? "is-done" : ""}>
          {dotIndex + 1 === index ? index : ""}
        </span>
      ))}
    </div>
  );
}

function MirroringScreen({
  user,
  onBack,
  onStart,
  loading,
  question,
  total,
  index,
  onSelect,
  sessionId,
  period,
  onPeriodChange,
  onFinish,
  score,
  mirroringStreakDays,
}: {
  user: VibeUser;
  onBack: () => void;
  onStart: (period: "day" | "week" | "month") => void;
  loading: boolean;
  question: MirroringQuestion | null;
  total: number;
  index: number;
  onSelect: (questionId: string, answer: string) => Promise<void>;
  sessionId: string;
  period: "day" | "week" | "month";
  onPeriodChange: (period: "day" | "week" | "month") => void;
  onFinish: () => void;
  score: MirroringResultPayload | null;
  mirroringStreakDays: number;
}) {
  const [answer, setAnswer] = useState("");
  const [hintOpen, setHintOpen] = useState(false);

  useEffect(() => {
    setAnswer("");
    setHintOpen(false);
  }, [question?.id]);

  if (loading && !question && !score) {
    return (
      <section className="b-app-screen b-mirroring-loading-screen">
        <AppTopbar title="Mirroring" onBack={onBack} />
        <section className="b-mirror-loading-card" aria-live="polite">
          <img alt="" src={missionAiImage} />
          <span>AI 오답노트 생성 중</span>
          <h2>최근 오답을 살펴보고<br />새 문제를 만들고 있어요.</h2>
          <p>잠시만 기다려 주세요.</p>
          <div className="b-mirror-loading-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </div>
        </section>
      </section>
    );
  }

  if (!sessionId && !question && !score) {
    return (
      <section className="b-app-screen b-mirroring-home-screen">
        <AppTopbar title="Mirroring" onBack={onBack} />
        <section className="b-mirror-profile-card">
          <img
            alt=""
            className={getUserThumbnailClass(user)}
            src={getUserThumbnailSrc(user)}
          />
          <div>
            <strong>{user.name || user.id}님</strong>
            <p>{getUserClassName(user)} · {formatKoreanDate(new Date())}</p>
          </div>
        </section>
        <section className="b-mirror-memory-card">
          <img alt="" src={missionAiImage} />
          <strong>지난 풀이를<br />돌아보는 시간이에요~!</strong>
          <div className="b-mirror-progress" aria-label="미러링 진행 1/5">
            <span className="is-active" />
            <span />
            <span className="is-number"><em>1/5</em></span>
            <span />
            <span />
          </div>
        </section>
        <section className="b-mirror-start-card">
          <span>Today Mirroring</span>
          <h2>오늘의 오답노트를<br />시작할까요?</h2>
          <div className="b-mirror-period-list">
            {MIRRORING_PERIOD_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => {
                  onPeriodChange(option.id);
                  onStart(option.id);
                }}
                type="button"
              >
                <span>{option.id === "day" ? "Daily" : option.id === "week" ? "Weekly" : "Monthly"}</span>
                <strong>{option.label}간 오답노트</strong>
                <em>›</em>
              </button>
            ))}
          </div>
        </section>
      </section>
    );
  }

  if (score && score.finished) {
    const analysis = score.analysis || {};
    const noteScore = Math.round(Number(analysis.noteScore ?? score.score ?? (score.total === 0 ? 0 : (score.correct / score.total) * 100)) || 0);
    const readingScore = Math.round(Number(analysis.readingScore ?? noteScore) || 0);
    const speakingScore = Math.round(Number(analysis.speakingScore ?? Math.max(0, noteScore - 5)) || 0);
    const mirrorScores = [
      { label: "Reading", value: readingScore, text: analysis.strengthPoint || "최근 리딩 오답을 다시 확인했어요." },
      { label: "Speaking", value: speakingScore, text: analysis.weakPoint || "스피킹 표현도 함께 복습했어요." },
      {
        label: "Mirroring",
        value: noteScore,
        text: `정답 ${score.correct}개 · 오답 ${score.wrong}개`,
      },
    ];

    return (
      <section className="b-app-screen b-mirroring-complete-screen">
        <AppTopbar title="결과" onBack={onBack} />
        <section className="b-mirror-complete-hero">
          <img alt="" src={speakingQuestionBearImage} />
          <span>{`연속 학습 ${mirroringStreakDays}일째`}</span>
          <h2>오늘 학습 완료!</h2>
          <p>오늘도 영어 실력이 쑥쑥 자라고 있어요.</p>
        </section>
        <section className="b-mirror-score-list">
          <h3>영역별 점수</h3>
          {mirrorScores.map(({ label, value, text }) => (
            <article key={label}>
              <strong>{label} <b>{value}점</b></strong>
              <p>{text}</p>
              <div><span style={{ width: `${value}%` }} /></div>
            </article>
          ))}
        </section>
        <section className="b-ai-coach-note">
          <strong>AI 코치 테디의 한마디</strong>
          <p>{analysis.weakGuide || analysis.strengthGuide || "선택한 답과 정답의 차이를 비교해서 다음 학습에 반영해 주세요."}</p>
        </section>
        <section className="b-mirror-strength-card">
          <h3>강점 & 연습 포인트</h3>
          <article>
            <strong>강점</strong>
            <p>{analysis.strengthPoint || "정답으로 처리한 문항의 핵심 표현은 잘 유지하고 있습니다."}</p>
            <p>{analysis.strengthGuide || "정답 문항의 표현을 소리 내어 한 번 더 말해보세요."}</p>
          </article>
          <article>
            <strong>연습 포인트</strong>
            <p>{analysis.weakPoint || "오답 문항"}</p>
            <p>{analysis.weakGuide || "빈칸 앞뒤 문맥을 함께 보는 연습을 해봐요."}</p>
          </article>
        </section>
        <button className="b-primary-mobile b-fixed-bottom-button" onClick={onFinish} type="button">
          홈으로
        </button>
      </section>
    );
  }

  if (!question) {
    return (
      <section className="b-app-screen b-mirroring-practice-screen">
        <AppTopbar title={getMirroringNotebookTitle(period)} onBack={onBack} />
        <ScreenMessage title="문제를 불러오는 중" message="잠시만 기다려주세요." />
      </section>
    );
  }

  const selected = answer;
  const rawSentence = (question.sentence || "빈칸에 들어갈 표현을 골라보세요. ____").trim();
  const blankToken = "__MIRROR_BLANK__";
  const sentenceWithBlank = /_{2,}/.test(rawSentence)
    ? rawSentence
    : rawSentence.includes(" ? ")
      ? rawSentence.replace(" ? ", " ____ ")
      : `${rawSentence} ____`;
  const sentenceParts = sentenceWithBlank.replace(/_{2,}/, blankToken).split(blankToken);
  const sentenceBeforeBlank = sentenceParts[0] || "";
  const sentenceAfterBlank = sentenceParts.slice(1).join(blankToken);
  const progressTotal = Math.max(total || 1, 1);
  const progressIndex = Math.min(Math.max(index + 1, 1), progressTotal);
  const notebookTitle = getMirroringNotebookTitle(period);

  const submit = async () => {
    if (loading || !selected) return;
    await onSelect(question.id, selected);
  };

  return (
    <section className={`b-app-screen b-mirroring-practice-screen ${selected ? "has-selected" : ""} ${hintOpen ? "has-hint" : ""}`}>
      <AppTopbar title={notebookTitle} onBack={onBack} />
      <div className="b-mirror-linear-progress">
        <span style={{ width: `${(progressIndex / progressTotal) * 100}%` }} />
      </div>
      <section className="b-mirror-question-title">
        <span>{progressIndex} / {progressTotal}</span>
        <h2>{question.prompt || "빈 칸에 들어갈 단어는?"}</h2>
      </section>
      <section className="b-mirror-fill-card">
        <div>
          <span>Tap Words</span>
          <strong>
            {sentenceBeforeBlank}
            <em>{selected || "?"}</em>
            {sentenceAfterBlank}
          </strong>
        </div>
        <div>
          <span>Choose</span>
          <div className="b-mirror-choice-list">
            {question.choices.map((choice) => (
              <button
                key={choice}
                className={selected === choice ? "is-selected" : ""}
                onClick={() => setAnswer(choice)}
                type="button"
              >
                {choice}
              </button>
            ))}
          </div>
        </div>
      </section>
      <button className="b-reading-hint-button" onClick={() => setHintOpen(true)} type="button">
        힌트가 필요해요 &gt;
      </button>
      <button className="b-primary-mobile b-fixed-bottom-button" disabled={!selected || loading} onClick={() => void submit()} type="button">
        {selected ? "다음" : "다음"}
      </button>
      {hintOpen ? (
        <>
          <div className="b-complete-dim" aria-hidden />
          <section className="b-mirror-hint-sheet" role="dialog" aria-modal="true">
            <img alt="" src={missionAiImage} />
            <strong>AI 코치 테디의 한마디</strong>
            <p>{question.explanation || "정답은 문장의 흐름과 가장 잘 어울리는 단어예요!"}</p>
            <button className="b-primary-mobile" onClick={() => setHintOpen(false)} type="button">
              확인
            </button>
          </section>
        </>
      ) : null}
    </section>
  );
}

function MobileDialog({
  title,
  message,
  cancelLabel,
  confirmLabel = "확인",
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  cancelLabel?: string;
  confirmLabel?: string;
  onCancel?: () => void;
  onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus();
    return () => {
      previousActiveElement?.focus();
    };
  }, []);

  return (
    <>
      <div className="b-complete-dim" aria-hidden />
      <section ref={dialogRef} className="b-complete-popup b-action-dialog" role="dialog" aria-modal="true" tabIndex={-1}>
        <strong>{title}</strong>
        <p>{message}</p>
        <div className={cancelLabel ? "b-dialog-actions has-cancel" : "b-dialog-actions"}>
          {cancelLabel ? (
            <button className="b-outline-mobile" onClick={onCancel} type="button">
              {cancelLabel}
            </button>
          ) : null}
          <button className="b-primary-mobile" onClick={onConfirm} type="button">
            {confirmLabel}
          </button>
        </div>
      </section>
    </>
  );
}

function MyPageScreen({
  user,
  onEditProfile,
  onOpenInquiry,
  onLogout,
  onWithdraw,
}: {
  user: VibeUser;
  onEditProfile: () => void;
  onOpenInquiry: () => void;
  onLogout: () => void;
  onWithdraw: () => void;
}) {
  const [dialog, setDialog] = useState<"logoutConfirm" | "logoutDone" | "withdrawConfirm" | "withdrawDone" | null>(null);

  return (
    <section className="b-app-screen b-mypage-screen">
      <header className="b-mypage-titlebar">
        <h1>마이페이지</h1>
      </header>
      <div className="b-mypage-container">
        <section className="b-mypage-profile-card">
          <figure>
            <img
              alt="내 프로필"
              className={getUserThumbnailClass(user)}
              src={getUserThumbnailSrc(user)}
            />
          </figure>
          <div>
            <strong>{user.name || user.id}님</strong>
            <p>{getUserClassName(user)}</p>
          </div>
        </section>
        <section className="b-mypage-menu-list" aria-label="마이페이지 메뉴">
          <button className="has-chevron" onClick={onEditProfile} type="button">
            <span>내 정보</span>
            <em>›</em>
          </button>
          <button className="has-chevron" onClick={onOpenInquiry} type="button">
            <span>1:1 문의</span>
            <em>›</em>
          </button>
          <button onClick={() => setDialog("logoutConfirm")} type="button">
            <span>로그아웃</span>
          </button>
          <button className="is-muted" onClick={() => setDialog("withdrawConfirm")} type="button">
            <span>회원 탈퇴</span>
          </button>
        </section>
      </div>
      {dialog === "logoutConfirm" ? (
        <MobileDialog
          cancelLabel="취소"
          confirmLabel="로그아웃"
          message="현재 계정에서 로그아웃하시겠습니까?"
          onCancel={() => setDialog(null)}
          onConfirm={() => setDialog("logoutDone")}
          title="로그아웃 확인"
        />
      ) : null}
      {dialog === "logoutDone" ? (
        <MobileDialog
          message="로그아웃이 완료되었습니다."
          onConfirm={onLogout}
          title="로그아웃 완료"
        />
      ) : null}
      {dialog === "withdrawConfirm" ? (
        <MobileDialog
          cancelLabel="취소"
          confirmLabel="탈퇴"
          message="회원 탈퇴 시 학습 이력과 문의 내역을 더 이상 확인할 수 없습니다."
          onCancel={() => setDialog(null)}
          onConfirm={() => setDialog("withdrawDone")}
          title="회원탈퇴 확인"
        />
      ) : null}
      {dialog === "withdrawDone" ? (
        <MobileDialog
          message="회원탈퇴 처리가 완료되었습니다."
          onConfirm={onWithdraw}
          title="회원탈퇴 완료"
        />
      ) : null}
    </section>
  );
}

function EditProfileScreen({
  user,
  onBack,
  onSubmit,
  onStatus,
}: {
  user: VibeUser;
  onBack: () => void;
  onSubmit: (payload: {
    name: string;
    phone: string;
    gender: string;
    birth: string;
    thumbnailUrl: string;
    password?: string;
  }) => Promise<void>;
  onStatus: string;
}) {
  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(formatPhone(user.phone || ""));
  const [gender, setGender] = useState(user.gender || "");
  const [birth, setBirth] = useState(toDateInputValue(user.birth || ""));
  const [thumbnailUrl, setThumbnailUrl] = useState(user.thumbnailUrl || "");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [saved, setSaved] = useState(false);
  const thumbnailInputId = "profile-thumbnail-file";
  const originalPhone = formatPhone(user.phone || "");
  const originalBirth = toDateInputValue(user.birth || "");
  const hasProfileEdits =
    name.trim() !== user.name ||
    phone.trim() !== originalPhone ||
    gender !== (user.gender || "") ||
    birth !== originalBirth ||
    thumbnailUrl.trim() !== (user.thumbnailUrl || "") ||
    (password.length >= 8 && password === passwordConfirm);

  const handleThumbnailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setThumbnailUrl(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasProfileEdits) return;
    await onSubmit({
      name,
      phone,
      gender,
      birth,
      thumbnailUrl,
      password: password.length >= 8 && password === passwordConfirm ? password : undefined,
    });
    setSaved(true);
  };

  return (
    <section className={`b-app-screen b-profile-edit-screen ${hasProfileEdits ? "has-edits" : "is-pristine"} ${saved ? "has-modal" : ""}`}>
      <AppTopbar title="내 정보 수정" onBack={onBack} />
      <form className="b-profile-edit-form" onSubmit={submit}>
        <section className="b-profile-thumbnail-editor">
          <figure>
            <img
              alt="프로필 썸네일"
              className={thumbnailUrl || user.thumbnailUrl ? undefined : "is-default-bear"}
              src={thumbnailUrl || getUserThumbnailSrc(user)}
            />
          </figure>
          <div className="b-thumbnail-change-panel">
            <strong>프로필 변경</strong>
            <label className="b-thumbnail-change-button" htmlFor={thumbnailInputId}>
              이미지 선택
            </label>
            <input
              accept="image/*"
              className="b-thumbnail-file-input"
              id={thumbnailInputId}
              onChange={handleThumbnailChange}
              type="file"
            />
          </div>
        </section>
        <label className="b-mobile-field">
          <span>이름 <b>*</b></span>
          <input readOnly type="text" value={name} />
          <em>*이름은 수정이 불가능합니다.</em>
        </label>
        <label className="b-mobile-field">
          <span>아이디 <b>*</b></span>
          <input readOnly type="text" value={user.id} />
          <em>*아이디는 수정이 불가능합니다.</em>
        </label>
        <label className="b-mobile-field">
          <span>비밀번호 <b>*</b></span>
          <input
            autoComplete="new-password"
            onChange={(event) => setPassword(event.target.value)}
            placeholder="비밀번호를 입력해 주세요."
            type="password"
            value={password}
          />
        </label>
        <label className="b-mobile-field">
          <span>비밀번호 확인 <b>*</b></span>
          <input
            autoComplete="new-password"
            onChange={(event) => setPasswordConfirm(event.target.value)}
            placeholder="비밀번호를 입력해 주세요."
            type="password"
            value={passwordConfirm}
          />
        </label>
        <label className="b-mobile-field">
          <span>연락처 <b>*</b></span>
          <input
            inputMode="numeric"
            onChange={(event) => setPhone(formatPhone(event.target.value))}
            type="text"
            value={phone}
          />
        </label>
        <label className="b-mobile-field">
          <span>이메일 <b>*</b></span>
          <input readOnly type="email" value={user.email} />
          <em>*이메일 수정은 고객센터에 문의해 주시기 바랍니다.</em>
        </label>
        <label className="b-mobile-field">
          <span>생년월일 <b>*</b></span>
          <input onChange={(event) => setBirth(event.target.value)} type="date" value={birth} />
        </label>
        <label className="b-mobile-field">
          <span>성별 <b>*</b></span>
          <div className="b-gender-control">
            <div>
              <button
                className={gender === "male" ? "is-selected" : ""}
                onClick={() => setGender("male")}
                type="button"
              >
                남성
              </button>
              <button
                className={gender === "female" ? "is-selected" : ""}
                onClick={() => setGender("female")}
                type="button"
              >
                여성
              </button>
            </div>
          </div>
        </label>
        {onStatus ? <p className="b-form-status">{onStatus}</p> : null}
        <button className="b-primary-mobile b-fixed-bottom-button" disabled={!hasProfileEdits} type="submit">
          수정하기
        </button>
      </form>
      {saved ? (
        <MobileDialog
          message="변경된 회원정보가 저장되었습니다."
          onConfirm={onBack}
          title="회원정보 수정 완료"
        />
      ) : null}
    </section>
  );
}

function InquiryListScreen({
  inquiries,
  onBack,
  onCreate,
  onOpen,
}: {
  inquiries: VibeInquiry[];
  onBack: () => void;
  onCreate: () => void;
  onOpen: (id: string) => void;
}) {
  const [activeFilter, setActiveFilter] = useState<(typeof INQUIRY_FILTERS)[number]>("전체");
  const filteredInquiries = activeFilter === "전체"
    ? inquiries
    : inquiries.filter((inquiry) => inquiry.category === activeFilter);

  return (
    <section className="b-app-screen b-inquiry-list-screen">
      <AppTopbar title="1:1 문의" onBack={onBack} />
      <section className="b-inquiry-list-heading">
        <h2>내 문의</h2>
        <strong>{inquiries.length}</strong>
      </section>
      <section className="b-inquiry-filter-tabs" aria-label="문의 유형 필터">
        {INQUIRY_FILTERS.map((filter) => (
          <button
            className={activeFilter === filter ? "is-active" : ""}
            key={filter}
            onClick={() => setActiveFilter(filter)}
            type="button"
          >
            {filter}
          </button>
        ))}
      </section>
      <section className="b-inquiry-list">
        {filteredInquiries.length === 0 ? (
          <section className="b-inquiry-empty-state">
            <strong>등록된 문의가 없습니다.</strong>
            <p>궁금한 내용을 남기면 관리자 답변을 확인할 수 있습니다.</p>
          </section>
        ) : (
          filteredInquiries.map((inquiry) => (
            <button key={inquiry.id} onClick={() => onOpen(inquiry.id)} type="button">
              <div>
                <span>{inquiry.status === "answered" ? "답변 완료" : "답변 대기"}</span>
                <strong>{inquiry.title.startsWith("[") ? inquiry.title : `[${inquiry.category}] ${inquiry.title}`}</strong>
                <p>{inquiry.createdAt}</p>
              </div>
            </button>
          ))
        )}
      </section>
      <button className="b-primary-mobile b-fixed-bottom-button" onClick={onCreate} type="button">
        1:1 문의하기
      </button>
    </section>
  );
}

function InquiryFormScreen({
  onBack,
  onSubmit,
}: {
  onBack: () => void;
  onSubmit: (payload: { category: string; title: string; body: string }) => Promise<void>;
}) {
  const [category, setCategory] = useState("서비스 이용 문의");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [completed, setCompleted] = useState(false);
  const canSubmit = title.trim().length > 0 && body.trim().length > 0;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    await onSubmit({ category, title: title.trim(), body: body.trim() });
    setCompleted(true);
  };

  return (
    <section className={`b-app-screen b-inquiry-form-screen ${canSubmit ? "is-ready" : "is-pristine"} ${completed ? "has-modal" : ""}`}>
      <AppTopbar title="문의하기" onBack={onBack} />
      <form className="b-inquiry-form" onSubmit={submit}>
        <label className="b-mobile-field">
          <span>문의 유형 <b>*</b></span>
          <select onChange={(event) => setCategory(event.target.value)} value={category}>
            <option>서비스 이용 문의</option>
            <option>탈퇴 문의</option>
            <option>환불 문의</option>
            <option>기타 문의</option>
          </select>
        </label>
        <label className="b-mobile-field">
          <span>문의 제목 <b>*</b></span>
          <input onChange={(event) => setTitle(event.target.value)} placeholder="문의 제목을 작성해 주세요." value={title} />
        </label>
        <label className="b-mobile-field">
          <span>문의 내용 <b>*</b></span>
          <textarea onChange={(event) => setBody(event.target.value)} placeholder="문의하실 내용을 입력해 주세요." value={body} />
        </label>
        <button className="b-primary-mobile b-fixed-bottom-button" disabled={!canSubmit} type="submit">
          완료
        </button>
      </form>
      {completed ? (
        <MobileDialog
          message="문의가 정상적으로 등록되었습니다."
          onConfirm={onBack}
          title="문의 등록 완료"
        />
      ) : null}
    </section>
  );
}

function InquiryDetailScreen({
  inquiry,
  onBack,
}: {
  inquiry: VibeInquiry;
  onBack: () => void;
}) {
  return (
    <section className="b-app-screen b-inquiry-detail-screen">
      <AppTopbar title="문의 상세" onBack={onBack} />
      <section className="b-inquiry-detail-card">
        <span>{inquiry.category}</span>
        <h2>{inquiry.title}</h2>
        <p>{inquiry.createdAt}</p>
        <article>
          <strong>문의 내용</strong>
          <p>{inquiry.body}</p>
        </article>
        <article>
          <strong>답변</strong>
          <p>{inquiry.answer || "아직 답변이 등록되지 않았습니다."}</p>
        </article>
      </section>
    </section>
  );
}

export default function VibeBApp() {
  const { view, context, navigate } = useVibeRouter();
  const [user, setUser] = useState<VibeUser | null>(() => readUserFromStorage());
  const [terms, setTerms] = useState<TermAgreementState>(INITIAL_TERM_STATE);
  const [termsDetailId, setTermsDetailId] = useState<TermId | null>(null);
  const [signupDraft, setSignupDraft] = useState<SignupProfilePayload | null>(null);
  const [signupPayload, setSignupPayload] = useState<SignupPayload | null>(null);
  const [signupEmailMessage, setSignupEmailMessage] = useState("");
  const [signupCompleteName, setSignupCompleteName] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState<VibeTicket | null>(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketChecked, setTicketChecked] = useState(false);
  const [ticketMessage, setTicketMessage] = useState("");
  const [systemDialog, setSystemDialog] = useState<{ title: string; message: string } | null>(null);
  const [notices, setNotices] = useState<VibeNotice[]>([]);
  const [inquiries, setInquiries] = useState<VibeInquiry[]>([]);
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [reviewKind, setReviewKind] = useState<"reading" | "speaking">("reading");
  const [reviews, setReviews] = useState<VibeReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState("");
  const [review, setReview] = useState<VibeReview | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [markingReview, setMarkingReview] = useState(false);
  const [reviewMarkMessage, setReviewMarkMessage] = useState("");
  const [recentLearning, setRecentLearning] = useState<RecentLearningItem[]>([]);
  const [reviewPractice, setReviewPractice] = useState<ReviewPracticeState | null>(null);
  const [missionCompletion, setMissionCompletion] = useState<MissionCompletionState>({});
  const [mirroringState, setMirroringState] = useState({
    period: "day" as "day" | "week" | "month",
    sessionId: "",
    question: null as MirroringQuestion | null,
    index: 0,
    total: 0,
    loading: false,
    result: null as MirroringResultPayload | null,
  });

  const requiresAuthViews = AUTH_REQUIRED_VIEWS;

  const allTermsChecked = useMemo(() => TERMS.every((term) => terms[term.id]), [terms]);
  const requiredTermsChecked = useMemo(
    () => TERMS.filter((term) => term.required).every((term) => terms[term.id]),
    [terms],
  );
  const missionStatus = useMemo(() => getDailyMissionStatus(missionCompletion), [missionCompletion]);
  const mirroringStreakDays = useMemo(() => getMirroringStudyStreak(missionCompletion), [missionCompletion]);
  const todayReviewWrongCount = useMemo(() => {
    const storedWrongCount = getTodayReviewWrongCount(missionCompletion);
    if (!reviewPractice?.result?.finished) return storedWrongCount;

    const savedResult = missionCompletion.reviewResults?.[reviewPractice.kind];
    if (savedResult?.date === getDateKey()) return storedWrongCount;
    return storedWrongCount + Math.max(0, reviewPractice.result.wrong);
  }, [missionCompletion, reviewPractice]);
  const handleTermToggle = useCallback((termId: TermId) => {
    setTerms((current) => ({ ...current, [termId]: !current[termId] }));
  }, []);
  const handleTermsAllToggle = useCallback(() => {
    const nextValue = !allTermsChecked;
    setTerms(() =>
      TERMS.reduce((next, term) => {
        next[term.id] = nextValue;
        return next;
      }, {} as TermAgreementState),
    );
  }, [allTermsChecked]);
  const handleTermOpenDetail = useCallback(
    (termId: TermId) => {
      setTermsDetailId((current) => (current === termId ? null : termId));
    },
    [],
  );

  useEffect(() => {
    writeUserToStorage(user);
  }, [user]);

  useEffect(() => {
    setMissionCompletion(readMissionCompletion(user));
  }, [user]);

  const markMissionCompleted = useCallback(
    (kind: "reading" | "speaking" | "mirroring", period: "day" | "week" | "month" = "day") => {
      setMissionCompletion((current) => {
        const next: MissionCompletionState =
          kind === "mirroring"
            ? {
                ...current,
                mirroring: {
                  ...(current.mirroring || {}),
                  [period]: getMissionCycleKey(period),
                },
                mirroringStudyDates: withMirroringStudyDate(current),
              }
            : {
                ...current,
                [kind]: getDateKey(),
              };

        writeMissionCompletion(user, next);
        return next;
      });
    },
    [user],
  );

  const markReviewPracticeCompleted = useCallback(
    (kind: "reading" | "speaking", result: { total: number; correct: number; wrong: number }) => {
      setMissionCompletion((current) => {
        const next: MissionCompletionState = {
          ...current,
          [kind]: getDateKey(),
          reviewResults: {
            ...(current.reviewResults || {}),
            [kind]: {
              date: getDateKey(),
              total: result.total,
              correct: result.correct,
              wrong: result.wrong,
            },
          },
        };

        writeMissionCompletion(user, next);
        return next;
      });
    },
    [user],
  );

  const showMissionCompletedDialog = useCallback((message = "오늘은 미션을 클리어 했어요. 다음 순차에 다시 진행할 수 있어요.") => {
    setSystemDialog({
      title: "미션 완료",
      message,
    });
  }, []);

  const withAuthGuard = useCallback(
    async (callback: () => Promise<void>) => {
      if (!user) {
        navigate("login");
        return;
      }
      await callback();
    },
    [user, navigate],
  );

  const loadTicket = useCallback(async () => {
    if (!user) {
      setTicket(null);
      setTicketChecked(false);
      return null;
    }
    try {
      const result = await requestApi<VibeTicket>("/api/v1/ticket/status");
      const nextTicket = result.data || null;
      setTicket(nextTicket);
      return nextTicket;
    } catch {
      setTicket(null);
      return null;
    } finally {
      setTicketChecked(true);
    }
  }, [user]);

  const loadNotices = useCallback(async () => {
    try {
      setNoticeLoading(true);
      const result = await requestApi<VibeNotice[]>("/api/v1/notices");
      const list = (result.data || []) as VibeNotice[];
      setNotices(list.map(normalizeNoticeCopy));
    } catch {
      setNotices([]);
    } finally {
      setNoticeLoading(false);
    }
  }, []);

  const loadInquiries = useCallback(async () => {
    if (!user) {
      setInquiries([]);
      return;
    }
    try {
      const result = await requestApi<VibeInquiry[]>("/api/v1/inquiries");
      const list = result.data || [];
      setInquiries(
        list.map((item) => ({
          ...item,
          status: item.status === "answered" || Boolean(item.answer) ? "answered" : "waiting",
          createdAt: formatDate(item.createdAt),
        })),
      );
    } catch {
      setInquiries([]);
    }
  }, [user]);

  const loadReviews = useCallback(async (kind: "reading" | "speaking") => {
    setReviewsLoading(true);
    setReviewsError("");
    try {
      const result = await requestApi<VibeReview[]>(`/api/v1/reviews?kind=${kind}`);
      setReviews(result.data || []);
    } catch (error) {
      setReviews([]);
      setReviewsError(error instanceof ApiError ? error.message : "리뷰 불러오기 실패");
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  const loadReviewDetail = useCallback(async (id: string) => {
    setReviewLoading(true);
    try {
      const result = await requestApi<VibeReview>(`/api/v1/reviews/${id}`);
      setReview(result.data || null);
      setReviewMarkMessage("");
    } catch {
      setReview(null);
      setReviewMarkMessage("리뷰를 조회하지 못했습니다.");
    } finally {
      setReviewLoading(false);
    }
  }, []);

  const syncCurrentUserProfile = useCallback(async (baseUser: VibeUser) => {
    writeUserToStorage(baseUser);
    try {
      const result = await requestApi<VibeUser>("/api/v1/users/me");
      const profileUser = toVibeUser(result.data);
      if (!profileUser) return baseUser;

      const nextUser: VibeUser = {
        ...baseUser,
        ...profileUser,
        id: baseUser.id || profileUser.id,
        email: baseUser.email || profileUser.email,
        provider: profileUser.provider || baseUser.provider,
        className: profileUser.className || baseUser.className,
        authToken: profileUser.authToken || baseUser.authToken,
        authTokenExpiresAt: profileUser.authTokenExpiresAt || baseUser.authTokenExpiresAt,
      };
      setUser(nextUser);
      writeUserToStorage(nextUser);
      return nextUser;
    } catch {
      return baseUser;
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    void syncCurrentUserProfile(user);
  }, [syncCurrentUserProfile, user?.id]);

  const openReviewPractice = useCallback(
    (nextReview: VibeReview) => {
      const questions = pickReviewPracticeQuestions(nextReview);
      if (questions.length === 0) {
        setSystemDialog({
          title: "복습 준비중",
          message: "현재 복습 가능한 오답이 없습니다.",
        });
        return;
      }

      setReviewPractice({
        reviewId: nextReview.id,
        kind: nextReview.kind,
        questions,
        index: 0,
        answers: {},
        result: null,
        speakingAnalysis: null,
      });
      navigate(nextReview.kind === "reading" ? "readingPractice" : "speakingPractice", { reviewId: nextReview.id });
    },
    [navigate],
  );

  const handleStartReviewPractice = useCallback(
    async (nextReview: VibeReview) => {
      if (isReviewMissionCompleted(missionCompletion, nextReview.kind)) {
        showMissionCompletedDialog("오늘은 미션을 클리어 했어요. 내일 다시 진행할 수 있어요.");
        return;
      }

      let sourceReview = nextReview;
      let sourceReviews = reviews;
      if (nextReview.kind === "speaking") {
        try {
          const refreshed = await requestApi<VibeReview[]>("/api/v1/reviews?kind=speaking");
          const refreshedReviews = refreshed.data || [];
          sourceReview = refreshedReviews.find((item) => item.id === nextReview.id) || refreshedReviews[0] || nextReview;
          sourceReviews = refreshedReviews.length ? refreshedReviews : [sourceReview];
          setReviews(refreshedReviews);
        } catch {
          sourceReviews = [nextReview, ...reviews.filter((item) => item.id !== nextReview.id)];
        }
      }

      const practiceReview =
        sourceReview.kind === "speaking" && pickReviewPracticeQuestions(sourceReview).length >= SPEAKING_PRACTICE_TYPE_ORDER.length
          ? sourceReview
          : sourceReview.kind === "speaking"
            ? createSpeakingPracticeReview(sourceReview, sourceReviews)
            : sourceReview;
      const practiceQuestionCount = pickReviewPracticeQuestions(practiceReview).length;
      if (nextReview.kind !== "speaking" && practiceQuestionCount === 0) {
        setSystemDialog({
          title: "학습 준비중",
          message: "학습 문제가 준비되면 시작할 수 있어요. 잠시 후 다시 시도해 주세요.",
        });
        return;
      }

      openReviewPractice(practiceReview);
    },
    [missionCompletion, openReviewPractice, reviews, showMissionCompletedDialog],
  );

  const completeReviewPractice = useCallback(() => {
    setReviewPractice(null);
    setReview(null);
    navigate("home");
  }, [navigate]);

  const loadRecentLearning = useCallback(async () => {
    try {
      const [rRes, sRes] = await Promise.all([
        requestApi<VibeReview[]>("/api/v1/reviews?kind=reading"),
        requestApi<VibeReview[]>("/api/v1/reviews?kind=speaking"),
      ]);

      const rows = [
        ...(rRes.data || []),
        ...(sRes.data || []),
      ].map((review) => ({
        id: review.id,
        title: review.title,
        date: review.assignedAt,
        kind: review.kind,
        status: review.status,
      }));
      rows.sort((a, b) => (new Date(b.date).getTime() || 0) - (new Date(a.date).getTime() || 0));
      setRecentLearning(rows.slice(0, 5));
    } catch {
      setRecentLearning([]);
    }
  }, []);

  useEffect(() => {
    if (user && (view === "home" || view === "noticeList" || view === "noticeDetail")) {
      void loadNotices();
      void loadTicket();
      void loadRecentLearning();
    }
  }, [user, view, loadNotices, loadTicket, loadRecentLearning]);

  useEffect(() => {
    if (user && (view === "inquiryList" || view === "inquiryDetail")) {
      void loadInquiries();
    }
  }, [user, view, loadInquiries]);

  useEffect(() => {
    if (!requiresAuthViews.has(view)) return;
    if (!user) {
      navigate("login");
      return;
    }
    if (!ticketChecked) {
      void loadTicket();
      return;
    }
  }, [requiresAuthViews, ticketChecked, user, view, navigate, loadTicket]);

  useEffect(() => {
    if (user && !requiresAuthViews.has(view)) {
      navigate("home");
    }
  }, [requiresAuthViews, user, view, navigate]);

  useEffect(() => {
    if (isTicketRequiredView(view) && (!ticketChecked || !ticket || ticket.isExpired)) return;
    if (view === "reading") {
      void loadReviews("reading");
      setReviewKind("reading");
      navigate("reading", {});
    }
    if (view === "speaking") {
      void loadReviews("speaking");
      setReviewKind("speaking");
      navigate("speaking", {});
    }
  }, [view, ticket, ticketChecked, loadReviews, navigate]);

  useEffect(() => {
    if (isTicketRequiredView(view) && (!ticketChecked || !ticket || ticket.isExpired)) return;
    if (view === "readingPractice" || view === "speakingPractice") {
      if (context.reviewId) {
        void loadReviewDetail(context.reviewId);
      }

      if (!reviewPractice) {
        setReviewPractice({
          reviewId: context.reviewId,
          kind: (view === "readingPractice" ? "reading" : "speaking") as "reading" | "speaking",
          questions: [],
          index: 0,
          answers: {},
          result: null,
        });
      }

      return;
    }

    if (!reviewPractice) return;
    if (view === "readingDetail" || view === "speakingDetail") return;
    setReviewPractice(null);
  }, [context.reviewId, loadReviewDetail, reviewPractice, ticket, ticketChecked, view]);

  useEffect(() => {
    if (isTicketRequiredView(view) && (!ticketChecked || !ticket || ticket.isExpired)) return;
    if (view === "readingDetail" && context.reviewId) {
      void loadReviewDetail(context.reviewId);
    }
    if (view === "speakingDetail" && context.reviewId) {
      void loadReviewDetail(context.reviewId);
    }
    if ((view === "readingPractice" || view === "speakingPractice") && context.reviewId) {
      void loadReviewDetail(context.reviewId);
    }
  }, [view, context.reviewId, ticket, ticketChecked, loadReviewDetail]);

  useEffect(() => {
    if ((view === "readingPractice" || view === "speakingPractice") && review && review.id === context.reviewId) {
      setReviewPractice((current) => {
        if (!current) {
          return {
            reviewId: review.id,
            kind: review.kind,
            questions: pickReviewPracticeQuestions(review),
            index: 0,
            answers: {},
            result: null,
            speakingAnalysis: null,
          };
        }

        if (current.reviewId !== review.id) {
          return {
            ...current,
            reviewId: review.id,
            kind: review.kind,
            questions: pickReviewPracticeQuestions(review),
            index: 0,
            answers: {},
            result: null,
            speakingAnalysis: null,
          };
        }

        if (
          current.kind === "speaking" &&
          current.questions.length >= SPEAKING_PRACTICE_TYPE_ORDER.length &&
          pickReviewPracticeQuestions(review).length < current.questions.length
        ) {
          return current;
        }

        if (current.questions.length === 0) {
          return {
            ...current,
            questions: pickReviewPracticeQuestions(review),
          };
        }

        return current;
      });
    }
  }, [context.reviewId, review, view]);

  useEffect(() => {
    if (view === "noticeDetail" && context.noticeId && notices.length > 0) {
      const target = notices.find((notice) => notice.id === context.noticeId);
      if (target) return;
      void requestApi<VibeNotice>(`/api/v1/notices/${context.noticeId}`).then((result) => {
        if (!result.data) return;
        setNotices((current) => [normalizeNoticeCopy(result.data as VibeNotice), ...current.filter((notice) => notice.id !== result.data?.id)]);
      });
    }
  }, [view, context.noticeId, notices]);

  const moveToTicketRegistration = useCallback(() => {
    setTicketMessage("이용권 등록 후 이용해주세요.");
    navigate("ticket");
  }, [navigate]);

  const ensureTicketForAccess = useCallback(
    (callback: () => void, targetView?: VibeView) => {
      if (targetView && !isTicketRequiredView(targetView)) {
        callback();
        return;
      }

      if (ticket && !ticket.isExpired) {
        callback();
        return;
      }

      if (!ticketChecked) {
        void loadTicket().then((nextTicket) => {
          if (nextTicket && !nextTicket.isExpired) {
            callback();
            return;
          }
          moveToTicketRegistration();
        });
        return;
      }

      moveToTicketRegistration();
    },
    [ticket, ticketChecked, loadTicket, moveToTicketRegistration],
  );

  const handleBottomMove = useCallback(
    (target: VibeView) => {
      ensureTicketForAccess(() => navigate(target), target);
    },
    [ensureTicketForAccess, navigate],
  );

  const activeTicketRequiredPath = isTicketRequiredView(view);

  useEffect(() => {
    if (!user || !activeTicketRequiredPath) return;
    if (!ticketChecked) {
      void loadTicket();
      return;
    }
    if (!ticket || ticket.isExpired) {
      setReviewPractice(null);
      setReview(null);
      moveToTicketRegistration();
    }
  }, [activeTicketRequiredPath, ticket, ticketChecked, user, loadTicket, moveToTicketRegistration]);

  const openNoticeById = () => {
    navigate("noticeList");
  };

  const handleLogin = async (idOrEmail: string, password: string) => {
    setLoading(true);
    setStatusMessage("");
    try {
      const result = await requestAuth<{ user: VibeUser }>("login", {
        idOrEmail: idOrEmail.trim(),
        password: password.trim(),
      });
      const nextUser = getApiEnvelopeUser(result);
      if (!nextUser) throw new Error("로그인 응답에 사용자 정보가 없습니다.");
      setUser(nextUser);
      await syncCurrentUserProfile(nextUser);
      setStatusMessage("로그인되었습니다.");
      navigate("home");
    } catch (error) {
      setStatusMessage(error instanceof ApiError ? error.message : "로그인 실패");
    } finally {
      setLoading(false);
    }
  };

  const handleKakaoLogin = async () => {
    setStatusMessage("");

    if (!KAKAO_REST_API_KEY) {
      setStatusMessage("카카오 REST API 키가 설정되지 않았습니다.");
      return;
    }

    window.location.assign(createKakaoAuthorizeUrl());
  };

  const handleKakaoCallback = async (code: string, redirectUri: string) => {
    if (!code) {
      setStatusMessage("카카오 로그인 인증 정보가 올바르지 않습니다.");
      return;
    }

    setLoading(true);
    setStatusMessage("");
    try {
      const result = await requestAuth<{ user: VibeUser }>("kakaoOAuth", {
        code,
        redirectUri,
      });
      const nextUser = getApiEnvelopeUser(result);
      if (!nextUser) throw new Error("카카오 로그인 응답이 비정상입니다.");
      setUser(nextUser);
      const syncedUser = await syncCurrentUserProfile(nextUser);
      setStatusMessage("카카오로 로그인되었습니다.");
      navigate(needsKakaoProfileCompletion(syncedUser) ? "editProfile" : "home");
    } catch (error) {
      setStatusMessage(error instanceof ApiError ? error.message : "카카오 로그인 실패");
    }
    setLoading(false);
  };

  const handleSignupProfileNext = async (payload: SignupProfilePayload) => {
    setStatusMessage("");
    setSignupDraft(payload);
    setSignupPayload(null);
    setSignupEmailMessage("");
    navigate("signupEmail");
  };

  const moveToSignupTerms = () => {
    setStatusMessage("");
    setSignupEmailMessage("");
    navigate("signupTerms");
  };

  const handleSignupEmailNext = (payload: SignupPayload, message: string) => {
    setStatusMessage("");
    setSignupPayload(payload);
    setSignupEmailMessage(message);
    navigate("signupEmailVerify");
  };

  const handleSignupComplete = async (payload: SignupPayload) => {
    setLoading(true);
    setStatusMessage("");
    try {
      const result = await requestAuth<{ user: VibeUser }>("signup", {
        id: payload.id,
        email: payload.email,
        password: payload.password,
        name: payload.name,
        phone: payload.phone,
        gender: payload.gender,
        birth: payload.birth,
        passwordConfirm: payload.passwordConfirm,
      });

      const joinedUser = getApiEnvelopeUser(result);
      if (!joinedUser) throw new Error("회원가입 응답이 비정상입니다.");
      if (!joinedUser) {
        throw new Error("회원가입 응답이 올바르지 않습니다.");
      }
      const completedUser: VibeUser = {
        ...joinedUser,
        id: joinedUser.id || payload.id,
        email: joinedUser.email || payload.email,
        name: joinedUser.name || payload.name,
        phone: joinedUser.phone || payload.phone,
        gender: joinedUser.gender || payload.gender,
        birth: joinedUser.birth || payload.birth,
      };
      setSignupDraft(null);
      setSignupPayload(null);
      setSignupEmailMessage("");
      setSignupCompleteName(completedUser.name || payload.name);
      setTerms(INITIAL_TERM_STATE);
      navigate("signupComplete");
    } catch (error) {
      setStatusMessage(error instanceof ApiError ? error.message : "회원가입 실패");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleFindIdComplete = () => {
    setStatusMessage("");
    navigate("login");
  };

  const handleLogout = () => {
    setUser(null);
    setTicket(null);
    setTicketChecked(false);
    setReviews([]);
    setReview(null);
    setRecentLearning([]);
    setReviewPractice(null);
    setSignupDraft(null);
    setSignupPayload(null);
    setSignupEmailMessage("");
    setSignupCompleteName("");
    setStatusMessage("");
    setTicketMessage("");
    navigate("login");
  };

  const handleWithdraw = () => {
    handleLogout();
  };

  const handleRegisterTicket = async (code: string): Promise<TicketSubmitResult> => {
    setTicketLoading(true);
    setTicketMessage("");
    try {
      const result = await requestApi<VibeTicket>("/api/v1/ticket/register", {
        method: "POST",
        body: JSON.stringify({ code }),
      });
      setTicket(result.data || null);
      setTicketChecked(true);
      setTicketMessage("등록이 완료되었습니다.");
      return {
        ok: true,
        message: "등록이 완료되었습니다.",
        ticket: result.data || null,
      };
    } catch (error) {
      const nextMessage = error instanceof ApiError ? error.message : "등록번호가 일치하지 않습니다.";
      setTicketMessage(nextMessage);
      setTicketChecked(true);
      return {
        ok: false,
        message: nextMessage,
      };
    } finally {
      setTicketLoading(false);
    }
  };

  const handleNavigateProtected = (target: "reading" | "speaking" | "mirroring") => {
    if (target === "reading" || target === "speaking" || target === "mirroring") {
      ensureTicketForAccess(() => navigate(target), target);
    }
  };

  const handleMarkReview = async () => {
    if (!review) return;
    setReviewMarkMessage("");
    setMarkingReview(true);
    try {
      await requestApi(`/api/v1/reviews/${review.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: "reviewed" }),
      });
      setReview({ ...review, status: "reviewed" });
      setReviewMarkMessage("복습 완료 처리했습니다.");
      setReviewKind(review.kind);
      void loadReviews(review.kind);
    } catch (error) {
      setReviewMarkMessage(error instanceof ApiError ? error.message : "상태 변경 실패");
    } finally {
      setMarkingReview(false);
    }
  };

  const handleSubmitReviewPracticeAnswer = async (answer: string, speakingAnalysis?: SpeakingAnalysisResult | null) => {
    if (!reviewPractice) return;
    const current = reviewPractice.questions[reviewPractice.index];
    if (!current) return;

    const next = reviewPractice.index + 1;
    const nextAnswers = {
      ...reviewPractice.answers,
      [current.id]: answer,
    };

    if (next >= reviewPractice.questions.length) {
      const result = calculateReviewPracticeScore(reviewPractice.questions, nextAnswers);
      markReviewPracticeCompleted(reviewPractice.kind, result);
      setRecentLearning((current) =>
        current.map((item) =>
          item.id === reviewPractice.reviewId ? { ...item, status: "reviewed" as const } : item,
        ),
      );
      try {
        await requestApi(`/api/v1/reviews/${reviewPractice.reviewId}/result`, {
          method: "POST",
          body: JSON.stringify({
            answers: nextAnswers,
            questions: reviewPractice.questions,
            total: result.total,
            correct: result.correct,
            wrong: result.wrong,
          }),
        });
        await requestApi(`/api/v1/reviews/${reviewPractice.reviewId}/status`, {
          method: "PATCH",
          body: JSON.stringify({ status: "reviewed" }),
        }).catch(() => undefined);
      } catch (error) {
        setStatusMessage(error instanceof ApiError ? error.message : "학습 결과 저장에 실패했습니다.");
      }
      setReviewPractice((previous) =>
        previous
          ? {
              ...previous,
              answers: nextAnswers,
              index: reviewPractice.questions.length - 1,
              result,
              speakingAnalysis: speakingAnalysis || previous.speakingAnalysis || null,
            }
          : null,
      );
      return;
    }

    setReviewPractice((previous) =>
      previous
        ? {
            ...previous,
            index: next,
            answers: nextAnswers,
            speakingAnalysis: speakingAnalysis || previous.speakingAnalysis || null,
          }
        : null,
    );
  };

  const updateProfile = async (payload: {
    name: string;
    phone: string;
    gender: string;
    birth: string;
    thumbnailUrl: string;
    password?: string;
  }) => {
    if (!user) return;
    try {
      const userPatch = await requestApi<VibeUser>("/api/v1/users/me", {
        method: "PATCH",
        body: JSON.stringify({
          name: payload.name,
          phone: payload.phone,
          gender: payload.gender,
          birth: payload.birth,
          ...(payload.password ? { password: payload.password } : {}),
        }),
      });
      let nextUser: VibeUser = {
        ...user,
        name: userPatch.data?.name || payload.name,
        phone: userPatch.data?.phone || payload.phone,
        gender: userPatch.data?.gender || payload.gender,
        birth: userPatch.data?.birth || payload.birth,
      };
      const nextThumbnailUrl = payload.thumbnailUrl.trim();
      if (nextThumbnailUrl && nextThumbnailUrl !== (user.thumbnailUrl || "")) {
        try {
          const thumb = await requestApi<{ thumbnailUrl: string }>("/api/v1/thumbnail", {
            method: "POST",
            body: JSON.stringify({ thumbnailUrl: nextThumbnailUrl }),
          });
          nextUser = { ...nextUser, thumbnailUrl: thumb.data?.thumbnailUrl || nextThumbnailUrl };
        } catch {
          nextUser = { ...nextUser, thumbnailUrl: nextThumbnailUrl };
        }
      }
      setUser(nextUser);
    } catch (error) {
      setStatusMessage(error instanceof ApiError ? error.message : "프로필 수정 실패");
      throw error;
    }
  };

  const handleInquirySubmit = async (payload: { category: string; title: string; body: string }) => {
    const localInquiry: VibeInquiry = {
      id: `inquiry-${Date.now()}`,
      category: payload.category,
      title: payload.title,
      body: payload.body,
      status: "waiting",
      createdAt: formatDate(new Date().toISOString()),
    };

    try {
      const result = await requestApi<Partial<VibeInquiry> & { state?: string }>("/api/v1/inquiry", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const saved = result.data || {};
      const nextInquiry: VibeInquiry = {
        ...localInquiry,
        id: typeof saved.id === "string" ? saved.id : localInquiry.id,
        category: typeof saved.category === "string" ? saved.category : localInquiry.category,
        title: typeof saved.title === "string" ? saved.title : localInquiry.title,
        body: typeof saved.body === "string" ? saved.body : localInquiry.body,
        answer: typeof saved.answer === "string" ? saved.answer : undefined,
        status: saved.status === "answered" || saved.state === "답변완료" ? "answered" : "waiting",
        createdAt: typeof saved.createdAt === "string" ? formatDate(saved.createdAt) : localInquiry.createdAt,
      };
      setInquiries((current) => [nextInquiry, ...current]);
    } catch {
      setInquiries((current) => [localInquiry, ...current]);
    }
  };

  const startMirroring = async (period: "day" | "week" | "month") => {
    if (isMirroringMissionCompleted(missionCompletion, period)) {
      showMissionCompletedDialog(`${getMirroringPeriodLabel(period)} 미러링 미션을 클리어 했어요. 다음 순차에 다시 진행할 수 있어요.`);
      return;
    }

    setMirroringState((current) => ({ ...current, loading: true }));
    try {
      const result = await requestApi<{ sessionId: string; questionCount: number; period: "day" | "week" | "month" }>(
        "/api/v1/mirroring/starts",
        {
          method: "POST",
          body: JSON.stringify({ period }),
        },
      );

      const nextId = result.data?.sessionId || "";
      setMirroringState((current) => ({
        ...current,
        loading: false,
        sessionId: nextId,
        total: result.data?.questionCount || 0,
        period,
      }));
      if (!nextId) {
        setSystemDialog({
          title: "미러링을 시작할 수 없어요.",
          message: "아직 미러링 문제가 생성되지 않았습니다. 학습 결과 저장 후 다시 시도해 주세요.",
        });
        setMirroringState((current) => ({ ...current, loading: false, sessionId: "", question: null, result: null, total: 0 }));
        return;
      }
      const next = await requestApi<MirroringQuestionPayload | { completed: true }>(`/api/v1/mirroring/${nextId}/next`);
      const nextPayload = next.data;
      if (!nextPayload) {
        setMirroringState((current) => ({ ...current, loading: false }));
        return;
      }
      if ("completed" in nextPayload && nextPayload.completed) {
        const score = await requestApi<MirroringResultPayload>(`/api/v1/mirroring/${nextId}/result`);
        markMissionCompleted("mirroring", period);
        setMirroringState((current) => ({
          ...current,
          loading: false,
          result: score.data || null,
          question: null,
          total: score.data?.total || 0,
        }));
          return;
      }
      if ("question" in nextPayload && "index" in nextPayload && "total" in nextPayload) {
        setMirroringState((current) => ({
          ...current,
          loading: false,
          question: nextPayload.question,
          index: nextPayload.index,
          total: nextPayload.total,
          result: null,
          sessionId: nextId,
        }));
        navigate("mirroringPractice");
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "오류로 미러링 문제를 생성하지 못했습니다. 잠시 후 다시 시도해 주세요.";
      const isNoWrongItem = message.includes("오답") && message.includes("없");
      setStatusMessage(message);
      setSystemDialog({
        title: isNoWrongItem ? `${getMirroringPeriodLabel(period)} 미러링 문제가 아직 생성되지 않았어요.` : "미러링을 시작할 수 없어요.",
        message: isNoWrongItem ? "리딩/스피킹 학습 결과가 저장된 뒤 미러링 문제가 생성됩니다. 잠시 후 다시 시도해 주세요." : message,
      });
      setMirroringState({
        period,
        sessionId: "",
        question: null,
        index: 0,
        total: 0,
        loading: false,
        result: null,
      });
    }
  };

  const submitMirroringAnswer = async (questionId: string, answer: string) => {
    if (!mirroringState.sessionId) return;
    if (mirroringState.sessionId.startsWith("local-mirroring")) {
      const isCorrect = answer === mirroringState.question?.correctAnswer;
      markMissionCompleted("mirroring", mirroringState.period);
      setMirroringState((current) => ({
        ...current,
        loading: false,
        question: null,
        result: {
          total: 1,
          correct: isCorrect ? 1 : 0,
          wrong: isCorrect ? 0 : 1,
          finished: true,
        },
      }));
      return;
    }
    setMirroringState((current) => ({ ...current, loading: true }));
    try {
      await requestApi(`/api/v1/mirroring/${mirroringState.sessionId}/answer`, {
        method: "PATCH",
        body: JSON.stringify({ questionId, answer }),
      });
      const next = await requestApi<MirroringQuestionPayload | { completed: true }>(`/api/v1/mirroring/${mirroringState.sessionId}/next`);
      const nextPayload = next.data;
      if (!nextPayload) {
        setMirroringState((current) => ({ ...current, loading: false }));
        return;
      }
      if ("completed" in nextPayload && nextPayload.completed) {
        const score = await requestApi<MirroringResultPayload>(
          `/api/v1/mirroring/${mirroringState.sessionId}/result`,
        );
        markMissionCompleted("mirroring", mirroringState.period);
        setMirroringState((current) => ({
          ...current,
          loading: false,
          question: null,
          result: score.data || null,
        }));
      } else if ("question" in nextPayload && "index" in nextPayload && "total" in nextPayload) {
        setMirroringState((current) => ({
          ...current,
          loading: false,
          question: nextPayload.question,
          index: nextPayload.index,
          total: nextPayload.total,
        }));
      } else {
        setMirroringState((current) => ({ ...current, loading: false }));
      }
    } catch (error) {
      setStatusMessage(error instanceof ApiError ? error.message : "답안 제출 실패");
      setMirroringState((current) => ({ ...current, loading: false }));
    }
  };

  const completeMirroring = () => {
    setMirroringState({
      period: "day",
      sessionId: "",
      question: null,
      index: 0,
      total: 0,
      loading: false,
      result: null,
    });
    navigate("home");
  };

  const activeNotice = useMemo(() => {
    return notices.find((notice) => notice.id === context.noticeId) || notices[0];
  }, [context.noticeId, notices]);
  const activeInquiry = useMemo(() => {
    return inquiries.find((inquiry) => inquiry.id === context.inquiryId) || inquiries[0];
  }, [context.inquiryId, inquiries]);
  return (
    <main className="b-webview-frame">
      {(() => {
        if (!requiresAuthViews.has(view) && !user) {
          switch (view) {
            case "kakaoCallback":
              return (
                <KakaoCallbackScreen
                  busy={loading}
                  message={statusMessage}
                  onBack={() => navigate("login")}
                  onResolve={handleKakaoCallback}
                />
              );
            case "signupProfile":
              return (
                <SignupProfileScreen
                  busyMessage={statusMessage}
                  onBack={() => navigate("signupTerms")}
                  onComplete={handleSignupProfileNext}
                />
              );
            case "signupEmail":
              return (
                <SignupEmailInputScreen
                  busyMessage={statusMessage}
                  draft={signupDraft}
                  onBack={() => navigate("signupProfile")}
                  onNext={handleSignupEmailNext}
                />
              );
            case "signupEmailVerify":
              return (
                <SignupEmailVerifyScreen
                  busyMessage={statusMessage}
                  draft={signupPayload}
                  initialMessage={signupEmailMessage}
                  onBack={() => navigate("signupEmail")}
                  onComplete={handleSignupComplete}
                />
              );
            case "signupComplete":
              return <SignupCompleteScreen name={signupCompleteName} onLogin={() => navigate("login")} />;
            case "findId":
              return (
                <FindIdScreen
                  onBack={() => navigate("login")}
                  onFind={handleFindIdComplete}
                />
              );
            case "findPassword":
              return <FindPasswordScreen onBack={() => navigate("login")} onComplete={() => navigate("login")} />;
            case "signupTerms":
              return (
                <TermsScreen
                  details={termsDetailId}
                  onBack={() => navigate("login")}
                  onAllToggle={handleTermsAllToggle}
                  onNext={() => navigate("signupProfile")}
                  onOpenDetail={handleTermOpenDetail}
                  onToggle={handleTermToggle}
                  values={terms}
                />
              );
            default:
              return (
                <LoginScreen
                  state={{ busy: loading, message: statusMessage }}
                  onMoveFindId={() => navigate("findId")}
                  onMoveFindPassword={() => navigate("findPassword")}
                  onKakao={handleKakaoLogin}
                  onLogin={handleLogin}
                  onMoveSignup={moveToSignupTerms}
                />
              );
          }
        }

        if (!user)
          return (
            <LoginScreen
              state={{ busy: loading, message: statusMessage }}
              onMoveFindId={() => navigate("findId")}
              onMoveFindPassword={() => navigate("findPassword")}
              onKakao={handleKakaoLogin}
              onLogin={handleLogin}
              onMoveSignup={moveToSignupTerms}
            />
          );

        if (view === "home") {
          return (
            <>
              <HomeScreen
                notices={notices}
                onOpenMypage={() => navigate("mypage")}
                onOpenNotice={openNoticeById}
                onOpenTicket={() => navigate("ticket")}
                onNavigateProtected={handleNavigateProtected}
                missionStatus={missionStatus}
                recentLearning={recentLearning}
                reviewWrongCount={todayReviewWrongCount}
                ticket={ticket}
                user={user}
              />
              <BottomNavigation activeTab="home" onMove={handleBottomMove} />
            </>
          );
        }

        if (view === "noticeList") {
          return (
            <>
              {noticeLoading ? <ScreenMessage title="로딩중" message="공지 목록을 가져오는 중입니다." /> : null}
              <NoticeListScreen
                notices={notices}
                onBack={() => navigate("home")}
                onOpen={(id) => navigate("noticeDetail", { noticeId: id })}
              />
            </>
          );
        }

        if (view === "noticeDetail" && activeNotice) {
          return (
            <NoticeDetailScreen notice={activeNotice} onBack={() => navigate("noticeList")} />
          );
        }

        if (view === "ticket") {
          return (
            <TicketScreen
              loading={ticketLoading}
              message={ticketMessage}
              onBack={() => navigate("home")}
              onComplete={() => navigate("home")}
              onSubmit={handleRegisterTicket}
            />
          );
        }

        if (view === "reading") {
          return (
            <>
              <ReviewListScreen
                data={reviews}
                error={reviewsError}
                kind="reading"
                loading={reviewsLoading}
                onBack={() => navigate("home")}
                onStartPractice={handleStartReviewPractice}
                user={user}
              />
              <BottomNavigation activeTab="reading" onMove={handleBottomMove} />
            </>
          );
        }

        if (view === "speaking") {
          return (
            <>
              <ReviewListScreen
                data={reviews}
                error={reviewsError}
                kind="speaking"
                loading={reviewsLoading}
                onBack={() => navigate("home")}
                onStartPractice={handleStartReviewPractice}
                user={user}
              />
              <BottomNavigation activeTab="speaking" onMove={handleBottomMove} />
            </>
          );
        }

        if (view === "readingDetail" || view === "speakingDetail") {
          if (reviewLoading) {
            return (
              <>
                <ScreenMessage title="불러오는 중..." message="잠시만 기다려 주세요." />
                <BottomNavigation
                  activeTab={review?.kind === "speaking" ? "speaking" : "reading"}
                  onMove={handleBottomMove}
                />
              </>
            );
          }

          if (!review) {
            return (
              <>
                <ScreenMessage title="리뷰 없음" message="요청한 리뷰를 찾지 못했습니다." />
                <BottomNavigation activeTab="home" onMove={handleBottomMove} />
              </>
            );
          }

          return (
            <>
              <ReviewDetailScreen
                marking={markingReview}
                onBack={() => navigate(review.kind)}
                onMarkReviewed={handleMarkReview}
                onStartPractice={() => handleStartReviewPractice(review)}
                review={review}
              />
              <BottomNavigation
                activeTab={review.kind}
                onMove={handleBottomMove}
              />
            </>
          );
        }

        if (view === "readingPractice" || view === "speakingPractice") {
          if (!reviewPractice) {
            return <ScreenMessage title="복습 정보를 불러오는 중" message="잠시만 기다려주세요." />;
          }

          const question = reviewPractice.questions[reviewPractice.index] ?? null;
          const score = reviewPractice.result;
          const title = reviewPractice.kind === "reading" ? "리딩" : "스피킹";
          return (
            <ReviewPracticeScreen
              index={reviewPractice.index}
              onBack={() => navigate(reviewPractice.kind)}
              onFinish={completeReviewPractice}
              onSelect={handleSubmitReviewPracticeAnswer}
              question={question}
              result={score}
              reviewId={reviewPractice.reviewId}
              reviewTitle={review ? review.title : "리뷰 복습"}
              speakingAnalysis={reviewPractice.speakingAnalysis}
              title={title}
              total={reviewPractice.questions.length}
              user={user}
            />
          );
        }

        if (view === "mirroring") {
          return (
            <>
              <MirroringScreen
                loading={mirroringState.loading}
                onBack={() => navigate("home")}
                onFinish={completeMirroring}
                onPeriodChange={(period) => setMirroringState((current) => ({ ...current, period }))}
                onSelect={submitMirroringAnswer}
                onStart={startMirroring}
                period={mirroringState.period}
                question={mirroringState.question}
                total={mirroringState.total}
                index={mirroringState.index}
                score={mirroringState.result}
                sessionId={mirroringState.sessionId}
                mirroringStreakDays={mirroringStreakDays}
                user={user}
              />
              <BottomNavigation activeTab="mirroring" onMove={handleBottomMove} />
            </>
          );
        }

        if (view === "mirroringPractice") {
          return (
            <MirroringScreen
              loading={mirroringState.loading}
              onBack={() => navigate("home")}
              onFinish={completeMirroring}
              onPeriodChange={(period) => setMirroringState((current) => ({ ...current, period }))}
              onSelect={submitMirroringAnswer}
              onStart={startMirroring}
              period={mirroringState.period}
              question={mirroringState.question}
              total={mirroringState.total}
              index={mirroringState.index}
              score={mirroringState.result}
              sessionId={mirroringState.sessionId}
              mirroringStreakDays={mirroringStreakDays}
              user={user}
            />
          );
        }

        if (view === "mypage") {
          return (
            <>
              <MyPageScreen
                onEditProfile={() => navigate("editProfile")}
                onOpenInquiry={() => navigate("inquiryList")}
                onLogout={handleLogout}
                onWithdraw={handleWithdraw}
                user={user}
              />
              <BottomNavigation activeTab="mypage" onMove={handleBottomMove} />
            </>
          );
        }

        if (view === "inquiryList") {
          return (
            <InquiryListScreen
              inquiries={inquiries}
              onBack={() => navigate("mypage")}
              onCreate={() => navigate("inquiryForm")}
              onOpen={(id) => navigate("inquiryDetail", { inquiryId: id })}
            />
          );
        }

        if (view === "inquiryForm") {
          return (
            <InquiryFormScreen
              onBack={() => navigate("inquiryList")}
              onSubmit={handleInquirySubmit}
            />
          );
        }

        if (view === "inquiryDetail" && activeInquiry) {
          return <InquiryDetailScreen inquiry={activeInquiry} onBack={() => navigate("inquiryList")} />;
        }

        if (view === "editProfile") {
          return (
            <EditProfileScreen
              onBack={() => navigate("mypage")}
              onStatus={statusMessage}
              onSubmit={updateProfile}
              user={user}
            />
          );
        }

        return (
          <>
            <HomeScreen
              notices={notices}
              onOpenMypage={() => navigate("mypage")}
              onOpenNotice={openNoticeById}
              onOpenTicket={() => navigate("ticket")}
              onNavigateProtected={handleNavigateProtected}
              missionStatus={missionStatus}
              recentLearning={recentLearning}
              reviewWrongCount={todayReviewWrongCount}
              ticket={ticket}
              user={user}
            />
            <BottomNavigation activeTab="home" onMove={handleBottomMove} />
          </>
        );
      })()}
      {systemDialog ? (
        <MobileDialog
          message={systemDialog.message}
          onConfirm={() => setSystemDialog(null)}
          title={systemDialog.title}
        />
      ) : null}
    </main>
  );
}

